import React, { useState, useRef, useEffect } from 'react';
import { Upload, Download, Plus, Trash2, Sun, Moon, Sunset, RotateCw, Lightbulb, Maximize2, Ruler, Palette, CloudSun, Undo2, Redo2, Copy, Clipboard, Save, FolderOpen, Clock, Users, Check, Eye, EyeOff, GripVertical, ChevronDown, ChevronRight, Presentation, X, GripHorizontal, Sparkles, LogIn, LogOut, Shield, Crown, LayoutGrid, List, Play, Square, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { toast } from '../hooks/use-toast';
import LightingCanvas from '../components/LightingCanvas';
import { jsPDF } from 'jspdf';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authFetch, readJSON } from '../lib/auth';
import UpgradeModal from '../components/UpgradeModal';
import FeedbackWidget, { FEEDBACK_LOCAL_KEY } from '../components/FeedbackWidget';
import BrandLogo from '../components/BrandLogo';

// Small dual-axis pad for aiming a beam. Drag the puck (or tap anywhere)
// inside the circle to set direction. Angle convention matches the canvas:
// 0° = down, 90° = right, 180° = up, 270° = left. Distance from center is
// ignored — only angle drives light.direction.
function BeamPanTiltPad({ direction, onChange, size = 84 }) {
  const ref = useRef(null);
  const draggingRef = useRef(false);
  const radius = size / 2 - 8;
  const rad = (direction * Math.PI) / 180;
  const px = size / 2 + Math.sin(rad) * radius;
  const py = size / 2 + Math.cos(rad) * radius;

  const updateFromPointer = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const dx = e.clientX - rect.left - size / 2;
    const dy = e.clientY - rect.top - size / 2;
    if (dx === 0 && dy === 0) return;
    let ang = (Math.atan2(dx, dy) * 180) / Math.PI;
    if (ang < 0) ang += 360;
    onChange(Math.round(ang));
  };

  return (
    <div
      ref={ref}
      onPointerDown={(e) => {
        draggingRef.current = true;
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
        updateFromPointer(e);
      }}
      onPointerMove={(e) => { if (draggingRef.current) updateFromPointer(e); }}
      onPointerUp={() => { draggingRef.current = false; }}
      onPointerCancel={() => { draggingRef.current = false; }}
      style={{ width: size, height: size }}
      className="relative rounded-full bg-slate-900/70 border border-slate-700 cursor-crosshair touch-none select-none flex-shrink-0"
      data-testid="beam-pan-tilt-pad"
      title="Drag to aim beam"
    >
      <div className="absolute inset-0 rounded-full pointer-events-none" style={{ boxShadow: 'inset 0 0 12px rgba(34,211,238,0.08)' }} />
      <div className="absolute inset-x-2 top-1/2 border-t border-slate-700/70 pointer-events-none" />
      <div className="absolute inset-y-2 left-1/2 border-l border-slate-700/70 pointer-events-none" />
      <div
        className="absolute w-4 h-4 rounded-full bg-cyan-400 pointer-events-none"
        style={{ left: px - 8, top: py - 8, boxShadow: '0 0 10px rgba(34,211,238,0.85)' }}
      />
      <div className="absolute inset-x-0 top-0.5 text-center text-[9px] text-slate-500 leading-none pointer-events-none">▲</div>
      <div className="absolute inset-x-0 bottom-0.5 text-center text-[9px] text-slate-500 leading-none pointer-events-none">▼</div>
    </div>
  );
}

const LightingStudio = () => {
  const navigate = useNavigate();
  const { user, signedIn, isAdmin, isPro, aiCredits, logout } = useAuth();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('AI Enhance');
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackContext, setFeedbackContext] = useState('manual');
  const [isSampleProject, setIsSampleProject] = useState(false);
  const [hasSample, setHasSample] = useState(false);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [ambientDarkness, setAmbientDarkness] = useState(50); // 0 = daylight, 100 = deep night
  const [lights, setLights] = useState([]);
  const [selectedLightId, setSelectedLightId] = useState(null);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [copiedLight, setCopiedLight] = useState(null);
  
  // Save/Load Projects state
  const [showRecentProjects, setShowRecentProjects] = useState(false);
  const [recentProjects, setRecentProjects] = useState([]);
  const loadProjectInputRef = useRef(null);
  
  // Preview mode state
  const [previewMode, setPreviewMode] = useState(false);
  
  // Light Grouping state
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedLights, setSelectedLights] = useState([]);
  const [showGroupMenu, setShowGroupMenu] = useState(false);
  const [hiddenGroups, setHiddenGroups] = useState([]); // Track hidden group IDs
  const [lightsViewMode, setLightsViewMode] = useState(() => {
    try {
      return localStorage.getItem('fls_lights_view_mode') === 'grid' ? 'grid' : 'list';
    } catch {
      return 'list';
    }
  });
  
  // Neon drawing mode state
  const [isDrawingNeon, setIsDrawingNeon] = useState(false);
  const [neonDrawingPoints, setNeonDrawingPoints] = useState([]);
  const [selectedNeonPointIndex, setSelectedNeonPointIndex] = useState(null);
  const [isSmartShapeMode, setIsSmartShapeMode] = useState(false); // For smart shape recognition
  
  // Presentation Mode state - for hyper-realism comparison view
  const [presentationMode, setPresentationMode] = useState(false);
  const [comparisonSliderPos, setComparisonSliderPos] = useState(50); // 0-100 percentage
  const [isDraggingSlider, setIsDraggingSlider] = useState(false);
  const presentationCanvasRef = useRef(null);
  const originalCanvasRef = useRef(null);
  
  // AI Enhancement state
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancedImage, setEnhancedImage] = useState(null);
  const [aiStrength, setAiStrength] = useState(0.35);
  
  // Sidebar resize state
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('sidebarWidth');
    return saved ? parseFloat(saved) : 25; // Default 25%
  });
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);
  
  // Collapsible sections state with localStorage persistence
  const [collapsedSections, setCollapsedSections] = useState(() => {
    const saved = localStorage.getItem('collapsedSections');
    return saved ? JSON.parse(saved) : {
      lights: false,
      groups: false,
      logos: false,
      occluders: false,
      tips: true
    };
  });
  
  const toggleSection = (section) => {
    setCollapsedSections(prev => {
      const newState = { ...prev, [section]: !prev[section] };
      localStorage.setItem('collapsedSections', JSON.stringify(newState));
      return newState;
    });
  };
  
  // Logo/Watermark state - Now supports multiple logos
  const [logos, setLogos] = useState([]); // Array of logo objects
  const [selectedLogoId, setSelectedLogoId] = useState(null); // Currently selected logo
  const logoInputRef = useRef(null);
  const [copiedLogo, setCopiedLogo] = useState(null); // For logo copy/paste
  
  // Get selected logo for controls
  const selectedLogo = logos.find(l => l.id === selectedLogoId);
  
  // Legacy single logo state for backward compatibility with canvas
  const logoImage = selectedLogo?.image || null;
  const logoPosition = selectedLogo?.position || { x: 50, y: 50 };
  const logoSize = selectedLogo?.size || 15;
  const logoRotation = selectedLogo?.rotation || 0;
  const logoOpacity = selectedLogo?.opacity || 50;
  const logoTiled = selectedLogo?.tiled || false;

  // Occluder state (architectural masks — soffits, overhangs, columns, balcony slabs)
  const [occluders, setOccluders] = useState([]);
  const [selectedOccluderId, setSelectedOccluderId] = useState(null);
  const [copiedOccluder, setCopiedOccluder] = useState(null);
  const [isDrawingOccluder, setIsDrawingOccluder] = useState(false);
  const selectedOccluder = occluders.find(o => o.id === selectedOccluderId);

  const handleAddOccluder = () => {
    const newOccluder = {
      id: Date.now(),
      name: `Occluder ${occluders.length + 1}`,
      type: 'rectangle',
      x: 50, // center %
      y: 50,
      width: 20, // % of canvas width
      height: 6, // % of canvas height (thin overhang by default)
      rotation: 0,
      softness: 12,
      opacity: 1,
      hidden: false,
    };
    setOccluders([...occluders, newOccluder]);
    setSelectedOccluderId(newOccluder.id);
    setSelectedLightId(null);
  };

  const handleUpdateOccluder = (id, updates) => {
    setOccluders(prev => prev.map(o => o.id === id ? { ...o, ...updates } : o));
  };

  const handleDeleteOccluder = (id) => {
    setOccluders(prev => prev.filter(o => o.id !== id));
    if (selectedOccluderId === id) setSelectedOccluderId(null);
  };

  const handleOccluderPositionChange = (id, x, y) => {
    setOccluders(prev => prev.map(o => {
      if (o.id !== id) return o;
      if (o.type === 'polygon' && Array.isArray(o.points) && o.points.length > 0) {
        // Translate all polygon points by delta (new centroid - old centroid)
        let cx = 0, cy = 0;
        o.points.forEach(p => { cx += p.x; cy += p.y; });
        cx /= o.points.length;
        cy /= o.points.length;
        const dx = x - cx;
        const dy = y - cy;
        const newPoints = o.points.map(p => ({
          x: Math.max(0, Math.min(100, p.x + dx)),
          y: Math.max(0, Math.min(100, p.y + dy)),
        }));
        return { ...o, points: newPoints };
      }
      return { ...o, x, y };
    }));
  };

  const handleOccluderResize = (id, updates) => {
    setOccluders(prev => prev.map(o => o.id === id ? { ...o, ...updates } : o));
  };

  // Resize a neon light by replacing its full path with a geometrically scaled one.
  const handleNeonResize = (id, newPath) => {
    setLights(prev => prev.map(l => (l.id === id ? { ...l, path: newPath } : l)));
    setHasUnsavedChanges(true);
  };

  const handleDuplicateOccluder = (id) => {
    const occ = occluders.find(o => o.id === id);
    if (!occ) return;
    const baseClone = {
      ...occ,
      id: Date.now(),
      name: `${occ.name || 'Occluder'} (copy)`,
    };
    let clone;
    if (occ.type === 'polygon' && Array.isArray(occ.points)) {
      clone = {
        ...baseClone,
        points: occ.points.map(p => ({
          x: Math.min(95, p.x + 5),
          y: Math.min(95, p.y + 5),
        })),
      };
    } else {
      clone = {
        ...baseClone,
        x: Math.min(95, occ.x + 5),
        y: Math.min(95, occ.y + 5),
      };
    }
    setOccluders(prev => [...prev, clone]);
    setSelectedOccluderId(clone.id);
    toast({ title: 'Occluder duplicated', description: 'Drag it to reposition' });
  };

  const handleCopyOccluder = () => {
    if (!selectedOccluder) return;
    // Deep clone with points array if polygon
    const copy = { ...selectedOccluder };
    if (Array.isArray(selectedOccluder.points)) {
      copy.points = selectedOccluder.points.map(p => ({ ...p }));
    }
    setCopiedOccluder(copy);
    toast({ title: 'Occluder copied', description: 'Press Ctrl+V to paste' });
  };

  const handlePasteOccluder = () => {
    if (!copiedOccluder) return;
    const base = {
      ...copiedOccluder,
      id: Date.now(),
      name: `${copiedOccluder.name || 'Occluder'} (paste)`,
    };
    let pasted;
    if (copiedOccluder.type === 'polygon' && Array.isArray(copiedOccluder.points)) {
      pasted = {
        ...base,
        points: copiedOccluder.points.map(p => ({
          x: Math.min(95, p.x + 5),
          y: Math.min(95, p.y + 5),
        })),
      };
    } else {
      pasted = {
        ...base,
        x: Math.min(95, copiedOccluder.x + 5),
        y: Math.min(95, copiedOccluder.y + 5),
      };
    }
    setOccluders(prev => [...prev, pasted]);
    setSelectedOccluderId(pasted.id);
    toast({ title: 'Occluder pasted' });
  };

  const handleToggleDrawOccluder = () => {
    setIsDrawingOccluder(prev => !prev);
    setSelectedOccluderId(null);
    setSelectedLightId(null);
  };

  const handleOccluderDrawingComplete = (data) => {
    // data may be either { type:'polygon', points:[...] } or { x,y,width,height }
    const common = {
      id: Date.now(),
      name: `Occluder ${occluders.length + 1}`,
      rotation: 0,
      softness: 12,
      opacity: 1,
      hidden: false,
    };
    let newOccluder;
    if (data.type === 'polygon' && Array.isArray(data.points)) {
      newOccluder = {
        ...common,
        type: 'polygon',
        points: data.points,
      };
    } else {
      newOccluder = {
        ...common,
        type: 'rectangle',
        x: data.x,
        y: data.y,
        width: data.width,
        height: data.height,
      };
    }
    setOccluders([...occluders, newOccluder]);
    setSelectedOccluderId(newOccluder.id);
    setIsDrawingOccluder(false);
    toast({ title: 'Occluder drawn', description: data.type === 'polygon' ? `${data.points.length} vertices captured` : 'Resize via corner handles or sliders' });
  };

  const updateSelectedOccluder = (updates) => {
    if (!selectedOccluderId) return;
    handleUpdateOccluder(selectedOccluderId, updates);
  };
  
  // Zoom and Pan state
  const [zoomLevel, setZoomLevel] = useState(1); // 1 = 100%, 2 = 200%, etc.
  // How the uploaded image fills the canvas area. Persisted across uploads.
  const [fitMode, setFitMode] = useState(() => {
    try {
      const v = localStorage.getItem('fls_fit_mode');
      return ['fit', 'width', 'height', 'custom'].includes(v) ? v : 'fit';
    } catch {
      return 'fit';
    }
  });
  const setFitModePersisted = (m) => {
    setFitMode(m);
    try { localStorage.setItem('fls_fit_mode', m); } catch (_) {}
  };
  
  // Custom canvas size state
  const [customWidth, setCustomWidth] = useState(() => {
    try {
      return parseInt(localStorage.getItem('fls_custom_width')) || 1920;
    } catch {
      return 1920;
    }
  });
  const [customHeight, setCustomHeight] = useState(() => {
    try {
      return parseInt(localStorage.getItem('fls_custom_height')) || 1080;
    } catch {
      return 1080;
    }
  });
  const [showCustomSizeInput, setShowCustomSizeInput] = useState(false);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 }); // Pan offset in pixels
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const lastPanOffsetRef = useRef({ x: 0, y: 0 });
  
  // Auto-save state
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const autoSaveIntervalRef = useRef(null);
  const AUTO_SAVE_INTERVAL = 3 * 60 * 1000; // 3 minutes (was 5) - more frequent to prevent data loss
  const SAVE_REMINDER_INTERVAL = 10 * 60 * 1000; // 10 minutes - remind user to save manually
  
  // History management for undo/redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedoAction = useRef(false);
  const saveTimerRef = useRef(null);
  
  // Track current values in refs to avoid stale closures
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  const selectedLight = lights.find(l => l.id === selectedLightId);
  
  // Keep refs in sync with state
  useEffect(() => {
    historyRef.current = history;
    historyIndexRef.current = historyIndex;
  }, [history, historyIndex]);

  // Save current state to history
  const saveStateToHistory = (currentLights, currentAmbient, currentSelected, currentGroups, currentOccluders, currentSelectedOccluder) => {
    if (isUndoRedoAction.current) return;
    
    const newState = {
      lights: JSON.parse(JSON.stringify(currentLights)),
      ambientDarkness: currentAmbient,
      selectedLightId: currentSelected,
      groups: JSON.parse(JSON.stringify(currentGroups)),
      occluders: JSON.parse(JSON.stringify(currentOccluders || [])),
      selectedOccluderId: currentSelectedOccluder
    };
    
    // Get current values from refs
    const currentHistory = historyRef.current;
    const currentIndex = historyIndexRef.current;
    
    // Remove any future history beyond current index
    const truncatedHistory = currentHistory.slice(0, currentIndex + 1);
    let newHistory = [...truncatedHistory, newState];
    let newIndex = currentIndex + 1;
    
    // Limit to 50 steps
    if (newHistory.length > 50) {
      newHistory.shift();
      newIndex = 49; // Keep index at max since we removed from start
    }
    
    // Update both states
    setHistory(newHistory);
    setHistoryIndex(newIndex);
  };

  // Watch for changes and save to history (debounced)
  useEffect(() => {
    if (!uploadedImage || isUndoRedoAction.current) return;
    
    // Clear existing timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    
    // Set new timer
    saveTimerRef.current = setTimeout(() => {
      saveStateToHistory(lights, ambientDarkness, selectedLightId, groups, occluders, selectedOccluderId);
    }, 300);
    
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [lights, ambientDarkness, selectedLightId, uploadedImage, groups, occluders, selectedOccluderId]);

  // Undo function
  const handleUndo = () => {
    if (historyIndex > 0) {
      isUndoRedoAction.current = true;
      const newIndex = historyIndex - 1;
      const state = history[newIndex];
      
      if (state) {
        setLights(JSON.parse(JSON.stringify(state.lights)));
        setAmbientDarkness(state.ambientDarkness);
        setSelectedLightId(state.selectedLightId);
        setGroups(JSON.parse(JSON.stringify(state.groups || [])));
        setOccluders(JSON.parse(JSON.stringify(state.occluders || [])));
        setSelectedOccluderId(state.selectedOccluderId || null);
        setHistoryIndex(newIndex);
        
        toast({
          title: 'Undo',
          description: 'Previous state restored',
        });
      }
      
      setTimeout(() => {
        isUndoRedoAction.current = false;
      }, 100);
    }
  };

  // Redo function
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      isUndoRedoAction.current = true;
      const newIndex = historyIndex + 1;
      const state = history[newIndex];
      
      if (state) {
        setLights(JSON.parse(JSON.stringify(state.lights)));
        setAmbientDarkness(state.ambientDarkness);
        setSelectedLightId(state.selectedLightId);
        setGroups(JSON.parse(JSON.stringify(state.groups || [])));
        setOccluders(JSON.parse(JSON.stringify(state.occluders || [])));
        setSelectedOccluderId(state.selectedOccluderId || null);
        setHistoryIndex(newIndex);
        
        toast({
          title: 'Redo',
          description: 'Next state restored',
        });
      }
      
      setTimeout(() => {
        isUndoRedoAction.current = false;
      }, 100);
    }
  };

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // ESC to exit neon drawing mode
      if (e.key === 'Escape' && isDrawingNeon) {
        e.preventDefault();
        setIsDrawingNeon(false);
        setIsSmartShapeMode(false);
        setNeonDrawingPoints([]);
        toast({
          title: 'Drawing mode ended',
          description: 'Select a light to edit or add a new one',
        });
        return;
      }

      // ESC to exit occluder drawing mode
      if (e.key === 'Escape' && isDrawingOccluder) {
        e.preventDefault();
        setIsDrawingOccluder(false);
        toast({ title: 'Occluder drawing cancelled' });
        return;
      }

      // ESC to deselect occluder (unlock selection)
      if (e.key === 'Escape' && selectedOccluderId && !isDrawingOccluder) {
        e.preventDefault();
        console.log('[ESC] Deselecting occluder:', selectedOccluderId);
        setSelectedOccluderId(null);
        toast({ 
          title: 'Occluder deselected', 
          description: 'You can now add lights or perform other actions',
          duration: 2000,
        });
        return;
      }

      // ESC to deselect light
      if (e.key === 'Escape' && selectedLightId && !isDrawingNeon) {
        e.preventDefault();
        console.log('[ESC] Deselecting light:', selectedLightId);
        setSelectedLightId(null);
        toast({ 
          title: 'Light deselected',
          duration: 2000,
        });
        return;
      }

      // Don't trigger shortcuts when typing in an input/textarea
      const tag = (e.target && e.target.tagName) || '';
      const typing = tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable;

      // Ctrl/Cmd+C — copy selected occluder
      if (!typing && (e.ctrlKey || e.metaKey) && e.key === 'c' && selectedOccluderId) {
        e.preventDefault();
        handleCopyOccluder();
        return;
      }
      // Ctrl/Cmd+V — paste occluder
      if (!typing && (e.ctrlKey || e.metaKey) && e.key === 'v' && copiedOccluder) {
        e.preventDefault();
        handlePasteOccluder();
        return;
      }
      // Ctrl/Cmd+D — duplicate selected occluder
      if (!typing && (e.ctrlKey || e.metaKey) && e.key === 'd' && selectedOccluderId) {
        e.preventDefault();
        handleDuplicateOccluder(selectedOccluderId);
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [historyIndex, history, isDrawingNeon, isDrawingOccluder, selectedOccluderId, copiedOccluder, occluders]);

  // Zoom control functions
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 4)); // Max 4x zoom
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => {
      const newZoom = Math.max(prev - 0.25, 1);
      // Reset pan if zooming back to 1x
      if (newZoom === 1) {
        setPanOffset({ x: 0, y: 0 });
      }
      return newZoom;
    });
  };

  const handleZoomReset = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };

  // Expose setZoomLevel for touch pinch-to-zoom in canvas
  useEffect(() => {
    window.setZoomLevelFromCanvas = (newZoom) => {
      setZoomLevel(newZoom);
      if (newZoom === 1) {
        setPanOffset({ x: 0, y: 0 });
      }
    };
    return () => {
      delete window.setZoomLevelFromCanvas;
    };
  }, []);

  // Auto-save functionality
  // Tracks whether we've already warned the user this session so a failing
  // auto-save loop doesn't spam toasts every interval tick.
  const autoSaveWarnedRef = useRef(false);

  const performAutoSave = () => {
    if (!uploadedImage || lights.length === 0) return;
    
    const autoSaveData = {
      version: '1.0',
      timestamp: new Date().toISOString(),
      image: uploadedImage,
      lights: lights,
      groups: groups,
      ambientDarkness: ambientDarkness,
      logos: logos, // Save as array
      occluders: occluders,
      sidebarWidth: sidebarWidth,
    };
    
    try {
      localStorage.setItem('facadeLightingAutoSave', JSON.stringify(autoSaveData));
      setLastAutoSave(new Date());
      setHasUnsavedChanges(false);
      autoSaveWarnedRef.current = false; // reset on successful save
      console.log('Auto-saved at', new Date().toLocaleTimeString());
    } catch (error) {
      console.error('Auto-save failed:', error);

      // Detect browser quota errors. Different browsers use different names/codes.
      const isQuotaError =
        error &&
        (error.name === 'QuotaExceededError' ||
          error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
          error.code === 22 ||
          error.code === 1014);

      // Only show the toast once per session to avoid spamming the user.
      if (!autoSaveWarnedRef.current) {
        autoSaveWarnedRef.current = true;
        if (isQuotaError) {
          toast({
            title: 'Auto-save unavailable — storage full',
            description:
              'Your browser ran out of local storage (large image). Click "Save Project" to download a backup to your device.',
            variant: 'destructive',
            duration: 8000,
          });
        } else {
          toast({
            title: 'Auto-save failed',
            description:
              (error && error.message) ||
              'Could not auto-save your work. Use "Save Project" to save manually.',
            variant: 'destructive',
            duration: 6000,
          });
        }
      }
    }
  };

  // Check for auto-saved data on mount
  useEffect(() => {
    // Check if recovering from a crash
    const crashRecovery = localStorage.getItem('facadeLighting_crashRecovery');
    
    const autoSaved = localStorage.getItem('facadeLightingAutoSave');
    if (autoSaved) {
      try {
        const data = JSON.parse(autoSaved);
        const savedTime = new Date(data.timestamp);
        const timeDiff = Date.now() - savedTime.getTime();
        const hoursDiff = timeDiff / (1000 * 60 * 60);
        
        // Only offer to restore if saved within last 24 hours
        if (hoursDiff < 24) {
          const isCrashRecovery = crashRecovery === 'true';
          
          toast({
            title: isCrashRecovery ? '🆘 Crash Recovery - Work Found!' : '🔄 Auto-saved work found',
            description: isCrashRecovery 
              ? `Your work was saved before an unexpected error. From ${savedTime.toLocaleString()}. Click to restore.`
              : `From ${savedTime.toLocaleString()}. Click to restore.`,
            duration: isCrashRecovery ? 30000 : 10000, // 30 seconds for crash recovery
            variant: isCrashRecovery ? 'destructive' : 'default',
            action: (
              <button
                onClick={() => {
                  restoreAutoSave(data);
                  localStorage.removeItem('facadeLighting_crashRecovery');
                }}
                className="px-3 py-1 bg-cyan-500 hover:bg-cyan-600 text-white rounded text-sm font-semibold"
              >
                Restore
              </button>
            ),
          });
          
          // Clear crash recovery flag after showing message
          if (isCrashRecovery) {
            console.log('[CRASH RECOVERY] Detected crash recovery data');
          }
        } else {
          // Old data, clear crash flag
          localStorage.removeItem('facadeLighting_crashRecovery');
        }
      } catch (error) {
        console.error('Failed to parse auto-save:', error);
        localStorage.removeItem('facadeLighting_crashRecovery');
      }
    } else {
      // No auto-save, clear crash flag
      localStorage.removeItem('facadeLighting_crashRecovery');
    }
  }, []);

  const restoreAutoSave = (data) => {
    if (data.image) setUploadedImage(data.image);
    if (data.lights) setLights(data.lights);
    if (data.groups) setGroups(data.groups);
    if (data.ambientDarkness !== undefined) setAmbientDarkness(data.ambientDarkness);
    // Handle multiple logos (new format) or single logo (legacy format)
    if (data.logos) {
      setLogos(data.logos);
      if (data.logos.length > 0) setSelectedLogoId(data.logos[0].id);
    } else if (data.logo && data.logo.image) {
      // Convert legacy single logo to array format
      const legacyLogo = {
        id: Date.now(),
        image: data.logo.image,
        position: data.logo.position || { x: 50, y: 50 },
        size: data.logo.size || 15,
        rotation: data.logo.rotation || 0,
        opacity: data.logo.opacity || 50,
        tiled: data.logo.tiled || false
      };
      setLogos([legacyLogo]);
      setSelectedLogoId(legacyLogo.id);
    }
    if (data.sidebarWidth) setSidebarWidth(data.sidebarWidth);
    if (data.occluders) setOccluders(data.occluders);
    
    toast({
      title: '✅ Work restored',
      description: 'Your auto-saved project has been restored',
    });
  };

  // Auto-save interval
  useEffect(() => {
    if (uploadedImage && lights.length > 0) {
      // Start auto-save interval (every 3 minutes)
      autoSaveIntervalRef.current = setInterval(() => {
        performAutoSave();
      }, AUTO_SAVE_INTERVAL);
      
      // Start save reminder interval (every 10 minutes)
      const reminderInterval = setInterval(() => {
        if (hasUnsavedChanges) {
          toast({
            title: '💾 Save Reminder',
            description: 'You have unsaved work. Click "Save Project" to secure your design.',
            duration: 5000,
          });
        }
      }, SAVE_REMINDER_INTERVAL);
      
      return () => {
        if (autoSaveIntervalRef.current) {
          clearInterval(autoSaveIntervalRef.current);
        }
        clearInterval(reminderInterval);
      };
    }
  }, [uploadedImage, lights.length, hasUnsavedChanges, performAutoSave, AUTO_SAVE_INTERVAL, SAVE_REMINDER_INTERVAL, toast]);
  
  // Emergency save when tab/window loses focus or visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && hasUnsavedChanges && uploadedImage && lights.length > 0) {
        console.log('[EMERGENCY] Tab hidden - performing emergency auto-save');
        performAutoSave();
        toast({
          title: 'Auto-saved before tab switch',
          duration: 2000,
        });
      }
    };
    
    const handleBlur = () => {
      if (hasUnsavedChanges && uploadedImage && lights.length > 0) {
        console.log('[EMERGENCY] Window blur - performing emergency save');
        performAutoSave();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
    };
  }, [hasUnsavedChanges, uploadedImage, lights.length, performAutoSave, toast]);

  // Mark as having unsaved changes when lights or other important state changes
  useEffect(() => {
    if (uploadedImage && lights.length > 0) {
      setHasUnsavedChanges(true);
    }
  }, [lights, groups, ambientDarkness, logoImage, logoPosition, logoSize, logoRotation, logoOpacity, logoTiled]);

  // Save before page unload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && uploadedImage && lights.length > 0) {
        performAutoSave();
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    // Catch unhandled errors and save before crash
    const handleError = (event) => {
      console.error('[CRASH PROTECTION] Unhandled error detected, performing emergency save', event.error);
      if (uploadedImage && lights.length > 0) {
        try {
          performAutoSave();
          localStorage.setItem('facadeLighting_crashRecovery', 'true');
        } catch (e) {
          console.error('[CRASH PROTECTION] Failed to save:', e);
        }
      }
    };
    
    const handleRejection = (event) => {
      console.error('[CRASH PROTECTION] Unhandled promise rejection, performing emergency save', event.reason);
      if (uploadedImage && lights.length > 0) {
        try {
          performAutoSave();
          localStorage.setItem('facadeLighting_crashRecovery', 'true');
        } catch (e) {
          console.error('[CRASH PROTECTION] Failed to save:', e);
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [hasUnsavedChanges, uploadedImage, lights]);

  // Handle mouse wheel zoom
  const handleWheelZoom = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoomLevel(prev => {
        const newZoom = Math.min(Math.max(prev + delta, 1), 4);
        if (newZoom === 1) {
          setPanOffset({ x: 0, y: 0 });
        }
        return newZoom;
      });
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target.result);
        toast({
          title: 'Image uploaded',
          description: 'Add lights to begin',
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddLight = (beamType) => {
    // Soft prompt if occluder is selected
    if (selectedOccluderId) {
      toast({
        title: '⚠️ Occluder is selected',
        description: 'Press ESC to deselect the occluder first, then add lights',
        duration: 3000,
        variant: 'default',
      });
      return;
    }

    if (beamType === 'neon') {
      // Enter neon drawing mode (freeform)
      setIsDrawingNeon(true);
      setIsSmartShapeMode(false);
      setShowAddMenu(false);
      toast({
        title: 'Neon Drawing Mode',
        description: 'Click and hold to draw freeform neon light path',
      });
      return;
    }
    
    if (beamType === 'smartshape') {
      // Enter smart shape neon drawing mode
      setIsDrawingNeon(true);
      setIsSmartShapeMode(true);
      setShowAddMenu(false);
      toast({
        title: 'Smart Shape Mode',
        description: 'Draw a shape - it will auto-correct to line, circle, rectangle, or polygon',
      });
      return;
    }
    
    // Exit neon drawing mode when selecting non-neon light types
    if (isDrawingNeon) {
      setIsDrawingNeon(false);
      setIsSmartShapeMode(false);
      setNeonDrawingPoints([]);
    }
    
    if (beamType === 'grazer') {
      // Add grazer linear light
      const newLight = {
        id: Date.now(),
        beamType: 'grazer',
        startPoint: { x: 45, y: 60 }, // Start position (percentage)
        endPoint: { x: 55, y: 60 },   // End position (percentage)
        length: 10, // Default length 10%
        rotation: 0, // Rotation angle
        throwDirection: 270, // Default upward (270°)
        throwDistance: 0.50, // How far light reaches — softer default (0.05–3 range)
        intensity: 20, // 20% brightness — softer default to avoid over-exposure
        colorTemp: 2300, // Default 2300K
        customColor: null,
        sourceSize: 1.0, // Light source/fixture size multiplier
        sourceHidden: false, // Toggle to hide the light source fixture
        surfaceThrow: false, // Realistic wall throw + bounce spill
      };
      setLights([...lights, newLight]);
      setSelectedLightId(newLight.id);
      setShowAddMenu(false);
      toast({
        title: 'Grazer light added',
        description: 'Drag line to move, drag end handles to resize',
      });
      return;
    }
    
    const spreadMap = { narrow: 25, medium: 45, wide: 70 };
    const newLight = {
      id: Date.now(),
      position: { x: 50, y: 40 },
      beamType,
      intensity: 50,
      direction: 180,
      spread: spreadMap[beamType],
      sourceSize: 0.5,
      beamLength: 0.2,
      colorTemp: 2200,
      customColor: null,
      sourceHidden: false, // Toggle to hide the light source
      surfaceThrow: false, // Realistic wall throw + bounce spill
      terminalRange: 0, // 0-100% — isolate last N% of the beam as a "terminal section"
      terminalTilt: 0, // -90..90° — bend the terminal section around its knee
    };
    setLights([...lights, newLight]);
    setSelectedLightId(newLight.id);
    setShowAddMenu(false);
    toast({
      title: 'Light added',
      description: 'Drag the light point to reposition it',
    });
  };

  // Shape recognition helper functions
  const recognizeShape = (points) => {
    if (points.length < 3) return { type: 'line', points };
    
    // Calculate bounding box
    const minX = Math.min(...points.map(p => p.x));
    const maxX = Math.max(...points.map(p => p.x));
    const minY = Math.min(...points.map(p => p.y));
    const maxY = Math.max(...points.map(p => p.y));
    const width = maxX - minX;
    const height = maxY - minY;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    
    // Check if shape is closed (start near end)
    const startPoint = points[0];
    const endPoint = points[points.length - 1];
    const closedThreshold = Math.max(width, height) * 0.15;
    const isClosed = Math.sqrt(
      Math.pow(endPoint.x - startPoint.x, 2) + 
      Math.pow(endPoint.y - startPoint.y, 2)
    ) < closedThreshold;
    
    // If not closed, check for line or curve
    if (!isClosed) {
      // Check if it's approximately a straight line
      const lineDeviation = calculateLineDeviation(points);
      if (lineDeviation < 0.05) {
        // It's a straight line
        return {
          type: 'line',
          points: [points[0], points[points.length - 1]]
        };
      }
      // It's a curve - smooth it
      return {
        type: 'curve',
        points: smoothCurve(points)
      };
    }
    
    // Closed shape - detect what kind
    // Calculate average radius from center (for circle detection)
    const distances = points.map(p => 
      Math.sqrt(Math.pow(p.x - centerX, 2) + Math.pow(p.y - centerY, 2))
    );
    const avgRadius = distances.reduce((a, b) => a + b, 0) / distances.length;
    const radiusVariance = distances.reduce((sum, d) => 
      sum + Math.pow(d - avgRadius, 2), 0) / distances.length;
    const radiusStdDev = Math.sqrt(radiusVariance) / avgRadius;
    
    // Detect corners FIRST — a near-square rectangle has a low radius
    // stddev (all four corners are equidistant from center) which would
    // otherwise get grabbed by the circle branch and misclassified.
    const corners = detectCorners(points, /* closed */ true);

    if (corners.length === 4) {
      // Check for rectangle (4 corners with ~90° angles)
      const isRectangle = checkRectangle(corners);
      if (isRectangle) {
        return {
          type: 'rectangle',
          points: createRectanglePoints(minX, minY, maxX, maxY)
        };
      }
    }

    // Triangle
    if (corners.length === 3) {
      return {
        type: 'triangle',
        points: [...corners, corners[0]] // Close the shape
      };
    }

    // Pentagon
    if (corners.length === 5) {
      return {
        type: 'pentagon',
        points: createRegularPolygon(centerX, centerY, avgRadius, 5)
      };
    }

    // Hexagon
    if (corners.length === 6) {
      return {
        type: 'hexagon',
        points: createRegularPolygon(centerX, centerY, avgRadius, 6)
      };
    }

    // Circle detection — accept slightly wobbly hand-drawn circles.
    // Runs AFTER polygon detection so near-square rectangles are not
    // mistaken for circles.
    if (radiusStdDev < 0.22) {
      return {
        type: 'circle',
        center: { x: centerX, y: centerY },
        radius: avgRadius
      };
    }
    
    // Other polygon or irregular shape - smooth it as closed curve
    return {
      type: 'polygon',
      points: corners.length >= 3 ? [...corners, corners[0]] : smoothCurve([...points, points[0]])
    };
  };
  
  const calculateLineDeviation = (points) => {
    if (points.length < 3) return 0;
    
    const start = points[0];
    const end = points[points.length - 1];
    const lineLength = Math.sqrt(
      Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
    );
    
    if (lineLength < 1) return 1; // Too short
    
    // Calculate perpendicular distance from each point to the line
    let maxDeviation = 0;
    for (const point of points) {
      const distance = Math.abs(
        (end.y - start.y) * point.x - 
        (end.x - start.x) * point.y + 
        end.x * start.y - 
        end.y * start.x
      ) / lineLength;
      
      maxDeviation = Math.max(maxDeviation, distance / lineLength);
    }
    
    return maxDeviation;
  };
  
  const smoothCurve = (points) => {
    if (points.length <= 4) return points;
    
    // Reduce points and smooth
    const step = Math.max(1, Math.floor(points.length / 20));
    const smoothed = [];
    
    for (let i = 0; i < points.length; i += step) {
      // Average nearby points for smoothing
      const start = Math.max(0, i - step);
      const end = Math.min(points.length - 1, i + step);
      let sumX = 0, sumY = 0, count = 0;
      
      for (let j = start; j <= end; j++) {
        sumX += points[j].x;
        sumY += points[j].y;
        count++;
      }
      
      smoothed.push({ x: sumX / count, y: sumY / count });
    }
    
    // Always include the last point
    if (smoothed[smoothed.length - 1] !== points[points.length - 1]) {
      smoothed.push(points[points.length - 1]);
    }
    
    return smoothed;
  };
  
  const detectCorners = (points, closed = false) => {
    if (points.length < 10) return points;
    
    const corners = [];
    // Only accept sharp direction changes (>~52°) as true corners. The old
    // 28° threshold flagged every noisy wobble as a corner, so hand-drawn
    // curves came out as triangles/polygons.
    const angleThreshold = 0.9;
    // Sample farther apart so single-pixel jitter doesn't create fake corners.
    const step = Math.max(3, Math.floor(points.length / 30));

    // For closed shapes we must also probe the wrap-around region so the
    // last corner (near start/end of the stroke) isn't missed — that used to
    // cause 4-corner rectangles to be detected as triangles.
    const N = points.length;
    const startI = closed ? 0 : step;
    const endI = closed ? N : (N - step);

    for (let i = startI; i < endI; i += step) {
      const prevIdx = closed ? ((i - step + N) % N) : Math.max(0, i - step);
      const nextIdx = closed ? ((i + step) % N) : Math.min(N - 1, i + step);
      const prev = points[prevIdx];
      const curr = points[i % N];
      const next = points[nextIdx];

      // Calculate angle change
      const angle1 = Math.atan2(curr.y - prev.y, curr.x - prev.x);
      const angle2 = Math.atan2(next.y - curr.y, next.x - curr.x);
      let angleDiff = Math.abs(angle2 - angle1);

      if (angleDiff > Math.PI) angleDiff = 2 * Math.PI - angleDiff;

      if (angleDiff > angleThreshold) {
        // Check if we already have a nearby corner
        const hasNearby = corners.some(c =>
          Math.sqrt(Math.pow(c.x - curr.x, 2) + Math.pow(c.y - curr.y, 2)) < 5
        );

        if (!hasNearby) {
          corners.push(curr);
        }
      }
    }

    return corners;
  };
  
  const checkRectangle = (corners) => {
    if (corners.length !== 4) return false;
    
    // Sort corners by angle from center
    const centerX = corners.reduce((sum, c) => sum + c.x, 0) / 4;
    const centerY = corners.reduce((sum, c) => sum + c.y, 0) / 4;
    
    corners.sort((a, b) => {
      const angleA = Math.atan2(a.y - centerY, a.x - centerX);
      const angleB = Math.atan2(b.y - centerY, b.x - centerX);
      return angleA - angleB;
    });
    
    // Check if opposite sides are roughly equal and angles are ~90°
    const d1 = Math.sqrt(Math.pow(corners[1].x - corners[0].x, 2) + Math.pow(corners[1].y - corners[0].y, 2));
    const d2 = Math.sqrt(Math.pow(corners[2].x - corners[1].x, 2) + Math.pow(corners[2].y - corners[1].y, 2));
    const d3 = Math.sqrt(Math.pow(corners[3].x - corners[2].x, 2) + Math.pow(corners[3].y - corners[2].y, 2));
    const d4 = Math.sqrt(Math.pow(corners[0].x - corners[3].x, 2) + Math.pow(corners[0].y - corners[3].y, 2));
    
    const ratio1 = Math.min(d1, d3) / Math.max(d1, d3);
    const ratio2 = Math.min(d2, d4) / Math.max(d2, d4);
    
    return ratio1 > 0.7 && ratio2 > 0.7;
  };
  
  const createRectanglePoints = (minX, minY, maxX, maxY) => {
    return [
      { x: minX, y: minY },
      { x: maxX, y: minY },
      { x: maxX, y: maxY },
      { x: minX, y: maxY },
      { x: minX, y: minY } // Close the shape
    ];
  };
  
  const createRegularPolygon = (centerX, centerY, radius, sides) => {
    const points = [];
    const angleStep = (2 * Math.PI) / sides;
    const startAngle = -Math.PI / 2; // Start from top
    
    for (let i = 0; i <= sides; i++) {
      const angle = startAngle + angleStep * i;
      points.push({
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle)
      });
    }
    
    return points;
  };
  
  const createCirclePoints = (centerX, centerY, radius, segments = 36) => {
    const points = [];
    for (let i = 0; i <= segments; i++) {
      const angle = (2 * Math.PI * i) / segments;
      points.push({
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle)
      });
    }
    return points;
  };

  const handleNeonDrawingComplete = (points) => {
    if (points.length < 2) {
      toast({
        title: 'Neon path too short',
        description: 'Draw a longer path',
        variant: 'destructive'
      });
      // Keep drawing mode active - don't exit
      setNeonDrawingPoints([]);
      return;
    }

    let finalPath = points;
    let shapeDescription = 'freeform neon';
    
    // Apply shape recognition if in smart shape mode
    if (isSmartShapeMode) {
      const recognized = recognizeShape(points);
      
      switch (recognized.type) {
        case 'line':
          finalPath = recognized.points;
          shapeDescription = 'straight line';
          break;
        case 'curve':
          finalPath = recognized.points;
          shapeDescription = 'smooth curve';
          break;
        case 'circle':
          finalPath = createCirclePoints(recognized.center.x, recognized.center.y, recognized.radius);
          shapeDescription = 'circle';
          break;
        case 'rectangle':
          finalPath = recognized.points;
          shapeDescription = 'rectangle';
          break;
        case 'triangle':
          finalPath = recognized.points;
          shapeDescription = 'triangle';
          break;
        case 'pentagon':
          finalPath = recognized.points;
          shapeDescription = 'pentagon';
          break;
        case 'hexagon':
          finalPath = recognized.points;
          shapeDescription = 'hexagon';
          break;
        case 'polygon':
          finalPath = recognized.points;
          shapeDescription = 'polygon';
          break;
        default:
          finalPath = points;
      }
    }

    const newNeonLight = {
      id: Date.now(),
      beamType: 'neon',
      path: finalPath, // Array of {x, y} in percentage
      thickness: 3, // Default thickness
      glowRadius: 20, // Default glow
      brightness: 50, // Default brightness (0-100)
      colorTemp: 2300, // Default 2300K
      customColor: null,
      intensity: 50, // For compatibility
      shapeType: isSmartShapeMode ? shapeDescription : 'freeform', // Track shape type
    };
    
    setLights([...lights, newNeonLight]);
    setSelectedLightId(newNeonLight.id);
    // Keep drawing mode active - don't exit neon mode
    // setIsDrawingNeon(false);  // Removed - stay in drawing mode
    // setIsSmartShapeMode(false);  // Removed - keep current mode
    setNeonDrawingPoints([]);
    
    toast({
      title: isSmartShapeMode ? `Smart shape: ${shapeDescription}` : 'Neon created',
      description: 'Keep drawing or press ESC / click Exit to stop',
    });
  };

  // Exit neon drawing mode
  const exitNeonDrawingMode = () => {
    setIsDrawingNeon(false);
    setIsSmartShapeMode(false);
    setNeonDrawingPoints([]);
    toast({
      title: 'Drawing mode ended',
      description: 'Select a light to edit or add a new one',
    });
  };

  const handleDeleteLight = (lightId) => {
    const lightToDelete = lights.find(l => l.id === lightId);
    if (!lightToDelete) return;
    
    const lightGroup = groups.find(g => g.lightIds.includes(lightId));
    
    if (lightGroup && selectedGroupId === lightGroup.id) {
      // Delete entire group
      const lightsToDelete = lightGroup.lightIds;
      setLights(lights.filter(light => !lightsToDelete.includes(light.id)));
      setGroups(groups.filter(g => g.id !== lightGroup.id));
      setSelectedLightId(null);
      setSelectedGroupId(null);
      
      toast({
        title: 'Group deleted',
        description: `${lightGroup.name} and all ${lightsToDelete.length} lights removed`,
      });
    } else {
      // Delete single light
      setLights(lights.filter(light => light.id !== lightId));
      
      // Remove light from any groups
      const updatedGroups = groups.map(group => ({
        ...group,
        lightIds: group.lightIds.filter(id => id !== lightId)
      })).filter(group => group.lightIds.length > 0); // Remove empty groups
      
      setGroups(updatedGroups);
      
      if (selectedLightId === lightId) {
        setSelectedLightId(null);
      }
      
      toast({
        title: 'Light deleted',
      });
    }
  };

  const updateSelectedLight = (updates) => {
    if (!selectedLightId) return;
    
    // Check if selected light is part of a group
    const lightGroup = groups.find(g => g.lightIds.includes(selectedLightId));
    
    if (lightGroup && selectedGroupId === lightGroup.id) {
      // Update ALL lights in the group
      setLights(lights.map(light => 
        lightGroup.lightIds.includes(light.id) ? { ...light, ...updates } : light
      ));
      
      toast({
        title: 'Group updated',
        description: `${lightGroup.name}: All ${lightGroup.lightIds.length} lights updated`,
      });
    } else {
      // Update only selected light
      setLights(lights.map(light => 
        light.id === selectedLightId ? { ...light, ...updates } : light
      ));
    }
  };

  const handleLightDrag = (lightId, newX, newY) => {
    const draggedLight = lights.find(l => l.id === lightId);
    if (!draggedLight) return;
    
    // Don't allow dragging neon or grazer lights (they have paths/lines, not single positions)
    if (draggedLight.beamType === 'neon' || draggedLight.beamType === 'grazer') return;
    
    const lightGroup = groups.find(g => g.lightIds.includes(lightId));
    
    if (lightGroup && selectedGroupId === lightGroup.id) {
      // Dragging a light that's part of the selected group - move entire group
      const deltaX = newX - draggedLight.position.x;
      const deltaY = newY - draggedLight.position.y;
      
      const updatedLights = lights.map(light => {
        if (lightGroup.lightIds.includes(light.id) && light.position) {
          return {
            ...light,
            position: {
              x: Math.max(0, Math.min(100, light.position.x + deltaX)),
              y: Math.max(0, Math.min(100, light.position.y + deltaY))
            }
          };
        }
        return light;
      });
      
      setLights(updatedLights);
    } else {
      // Dragging individual light
      setLights(lights.map(light => 
        light.id === lightId && light.position
          ? { ...light, position: { x: newX, y: newY } } 
          : light
      ));
    }
  };

  const handleGrazerDrag = (lightId, part, newX, newY) => {
    setLights(lights.map(light => {
      if (light.id !== lightId || light.beamType !== 'grazer') return light;
      
      if (part === 'start') {
        // Dragging start handle
        return {
          ...light,
          startPoint: { x: newX, y: newY },
          length: Math.sqrt(
            Math.pow(light.endPoint.x - newX, 2) + 
            Math.pow(light.endPoint.y - newY, 2)
          )
        };
      } else if (part === 'end') {
        // Dragging end handle
        return {
          ...light,
          endPoint: { x: newX, y: newY },
          length: Math.sqrt(
            Math.pow(newX - light.startPoint.x, 2) + 
            Math.pow(newY - light.startPoint.y, 2)
          )
        };
      } else if (part === 'line') {
        // Dragging entire line
        const deltaX = newX - (light.startPoint.x + light.endPoint.x) / 2;
        const deltaY = newY - (light.startPoint.y + light.endPoint.y) / 2;
        return {
          ...light,
          startPoint: {
            x: Math.max(0, Math.min(100, light.startPoint.x + deltaX)),
            y: Math.max(0, Math.min(100, light.startPoint.y + deltaY))
          },
          endPoint: {
            x: Math.max(0, Math.min(100, light.endPoint.x + deltaX)),
            y: Math.max(0, Math.min(100, light.endPoint.y + deltaY))
          }
        };
      }
      return light;
    }));
  };

  const handleCopyLight = () => {
    if (!selectedLightId) return;
    
    const lightGroup = groups.find(g => g.lightIds.includes(selectedLightId));
    
    if (lightGroup && selectedGroupId === lightGroup.id) {
      // Copy entire group
      const groupLights = lights.filter(light => lightGroup.lightIds.includes(light.id));
      setCopiedLight({
        type: 'group',
        groupName: lightGroup.name,
        lights: groupLights.map(light => ({ ...light }))
      });
      toast({
        title: 'Group copied',
        description: `${lightGroup.name} with ${groupLights.length} lights copied`,
      });
    } else {
      // Copy single light
      const light = lights.find(l => l.id === selectedLightId);
      if (light) {
        setCopiedLight({
          type: 'single',
          light: { ...light }
        });
        toast({
          title: 'Light copied',
          description: 'Ready to paste',
        });
      }
    }
  };

  const handlePasteLight = () => {
    if (!copiedLight) return;
    
    if (copiedLight.type === 'group') {
      // Paste entire group
      const newLights = copiedLight.lights.map(light => {
        const newLight = {
          ...light,
          id: Date.now() + Math.random(),
        };
        
        // Handle different light types
        if (light.beamType === 'grazer') {
          // Grazer light - offset startPoint and endPoint
          newLight.startPoint = {
            x: Math.min(95, light.startPoint.x + 5),
            y: Math.min(95, light.startPoint.y + 5)
          };
          newLight.endPoint = {
            x: Math.min(95, light.endPoint.x + 5),
            y: Math.min(95, light.endPoint.y + 5)
          };
        } else if (light.beamType === 'neon') {
          // Neon light - offset entire path
          newLight.path = light.path.map(point => ({
            x: Math.min(95, point.x + 5),
            y: Math.min(95, point.y + 5)
          }));
        } else if (light.position) {
          // Regular beam light - offset position
          newLight.position = {
            x: Math.min(95, light.position.x + 5),
            y: Math.min(95, light.position.y + 5)
          };
        }
        
        return newLight;
      });
      
      const newLightIds = newLights.map(l => l.id);
      
      // Create new group
      const newGroup = {
        id: Date.now(),
        name: `${copiedLight.groupName} (Copy)`,
        lightIds: newLightIds
      };
      
      setLights([...lights, ...newLights]);
      setGroups([...groups, newGroup]);
      setSelectedLightId(newLights[0].id);
      setSelectedGroupId(newGroup.id);
      
      toast({
        title: 'Group pasted',
        description: `${newGroup.name} with ${newLights.length} lights created`,
      });
    } else {
      // Paste single light
      const newLight = {
        ...copiedLight.light,
        id: Date.now(),
      };
      
      // Handle different light types
      if (copiedLight.light.beamType === 'grazer') {
        // Grazer light - offset startPoint and endPoint
        newLight.startPoint = {
          x: Math.min(95, copiedLight.light.startPoint.x + 5),
          y: Math.min(95, copiedLight.light.startPoint.y + 5)
        };
        newLight.endPoint = {
          x: Math.min(95, copiedLight.light.endPoint.x + 5),
          y: Math.min(95, copiedLight.light.endPoint.y + 5)
        };
      } else if (copiedLight.light.beamType === 'neon') {
        // Neon light - offset entire path
        newLight.path = copiedLight.light.path.map(point => ({
          x: Math.min(95, point.x + 5),
          y: Math.min(95, point.y + 5)
        }));
      } else if (copiedLight.light.position) {
        // Regular beam light - offset position
        newLight.position = {
          x: Math.min(95, copiedLight.light.position.x + 5),
          y: Math.min(95, copiedLight.light.position.y + 5)
        };
      }
      
      setLights([...lights, newLight]);
      setSelectedLightId(newLight.id);
      toast({
        title: 'Light pasted',
        description: 'New light created with copied settings',
      });
    }
  };

  // Logo Copy/Paste functions
  const handleCopyLogo = () => {
    if (!selectedLogo) return;
    
    setCopiedLogo({
      image: selectedLogo.image,
      position: { ...selectedLogo.position },
      size: selectedLogo.size,
      rotation: selectedLogo.rotation,
      opacity: selectedLogo.opacity,
      tiled: selectedLogo.tiled
    });
    
    toast({
      title: 'Logo copied',
      description: 'Logo settings copied to clipboard',
    });
  };

  const handlePasteLogo = () => {
    if (!copiedLogo) return;
    
    // Create a NEW logo with copied settings (offset position)
    const newLogo = {
      id: Date.now(),
      image: copiedLogo.image,
      position: {
        x: Math.min(90, copiedLogo.position.x + 10),
        y: Math.min(90, copiedLogo.position.y + 10)
      },
      size: copiedLogo.size,
      rotation: copiedLogo.rotation,
      opacity: copiedLogo.opacity,
      tiled: copiedLogo.tiled
    };
    
    setLogos(prev => [...prev, newLogo]);
    setSelectedLogoId(newLogo.id);
    
    toast({
      title: 'Logo pasted',
      description: 'New logo added with copied settings',
    });
  };

  // Group visibility toggle
  const toggleGroupVisibility = (groupId) => {
    setHiddenGroups(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  // Watermark helper — adds a "Facade Lighting Studio" watermark badge 
  // in the bottom-right of the export canvas for free-tier users.
  // Pro users don't see the watermark. 
  const drawExportWatermark = (canvas) => {
    if (isPro) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.save();
    const padding = Math.max(12, Math.round(canvas.width * 0.012));
    const fontSize = Math.max(14, Math.round(canvas.width * 0.016));
    const smallFont = `500 ${Math.round(fontSize * 0.85)}px system-ui, -apple-system, "Segoe UI", sans-serif`;
    const boldFont = `700 ${fontSize}px system-ui, -apple-system, "Segoe UI", sans-serif`;
    const brandText = 'Facade Lighting Studio';
    const ctaText = 'Upgrade to remove watermark';

    ctx.font = boldFont;
    const brandWidth = ctx.measureText(brandText).width;
    ctx.font = smallFont;
    const ctaWidth = ctx.measureText(ctaText).width;

    const innerWidth = Math.max(brandWidth, ctaWidth);
    const lineHeight = fontSize + 4;
    const boxHeight = lineHeight * 2 + padding;
    const boxWidth = innerWidth + padding * 2;
    const x = canvas.width - boxWidth - padding;
    const y = canvas.height - boxHeight - padding;

    // Backdrop pill
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    const r = Math.min(boxHeight / 2, 18);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + boxWidth - r, y);
    ctx.quadraticCurveTo(x + boxWidth, y, x + boxWidth, y + r);
    ctx.lineTo(x + boxWidth, y + boxHeight - r);
    ctx.quadraticCurveTo(x + boxWidth, y + boxHeight, x + boxWidth - r, y + boxHeight);
    ctx.lineTo(x + r, y + boxHeight);
    ctx.quadraticCurveTo(x, y + boxHeight, x, y + boxHeight - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fill();

    // Brand line — amber bold
    ctx.font = boldFont;
    ctx.fillStyle = 'rgba(255, 200, 80, 0.95)';
    ctx.textBaseline = 'middle';
    ctx.fillText(brandText, x + padding, y + padding / 2 + lineHeight / 2);

    // CTA line — softer color, smaller
    ctx.font = smallFont;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.fillText(ctaText, x + padding, y + padding / 2 + lineHeight + lineHeight / 2);

    ctx.restore();
  };

  // Check (and atomically record) an export with the backend quota gate.
  // Returns true if allowed. On 402 free-tier exhaustion, opens UpgradeModal.
  const checkExportQuota = async (type) => {
    if (!signedIn) {
      toast({
        title: 'Sign in required',
        description: 'Create a free account to export your design.',
      });
      navigate('/login');
      return false;
    }
    try {
      const res = await authFetch('/api/exports/record', {
        method: 'POST',
        body: JSON.stringify({ type }),
      });
      const data = await readJSON(res);
      if (res.status === 402) {
        setUpgradeReason('Export limit reached');
        setShowUpgradeModal(true);
        return false;
      }
      if (!res.ok) {
        toast({
          title: 'Export failed',
          description: data?.detail?.message || data?.detail || 'Could not verify export quota.',
          variant: 'destructive',
        });
        return false;
      }
      // Friendly nudge when a free user is close to the limit.
      if (data && data.pro === false && typeof data.remaining === 'number' && data.remaining <= 1) {
        toast({
          title:
            data.remaining === 0
              ? 'Last free export this month'
              : `${data.remaining} free export left this month`,
          description: 'Upgrade to Pro for unlimited exports and no watermark.',
        });
      }
      return true;
    } catch (e) {
      toast({
        title: 'Export failed',
        description: 'Network error — please try again.',
        variant: 'destructive',
      });
      return false;
    }
  };

  const handleExport = async () => {
    if (canvasRef.current) {
      // Server-side quota check first (free users: 3 exports / month).
      const allowed = await checkExportQuota('png');
      if (!allowed) return;

      // Enable preview mode to hide selection indicators
      const wasInPreview = previewMode;
      setPreviewMode(true);
      
      // Wait for canvas to re-render without selections
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const canvas = canvasRef.current.getCanvas();
      if (canvas) {
        drawExportWatermark(canvas);
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `facade-lighting-${Date.now()}.png`;
          link.click();
          URL.revokeObjectURL(url);
          
          // Restore preview mode state
          setPreviewMode(wasInPreview);
          
          toast({
            title: 'Exported',
            description: isPro ? 'Watermark-free Pro export' : 'Free export — upgrade to Pro to remove watermark',
          });

          // After first successful export, prompt for feedback (one-time per browser).
          try {
            if (!localStorage.getItem(FEEDBACK_LOCAL_KEY)) {
              setFeedbackContext('post_export');
              // Slight delay so the export toast has time to surface first.
              setTimeout(() => setShowFeedback(true), 1500);
            }
          } catch (_) {}
        });
      }
    }
  };

  // AI Enhancement - Finalize with Gemini Nano Banana (Pro feature)
  const handleAIEnhance = async () => {
    if (!signedIn) {
      toast({
        title: 'Sign in required',
        description: 'Create a free account to use AI Enhance.',
      });
      navigate('/login');
      return;
    }

    if (!presentationCanvasRef.current) {
      toast({
        title: 'Error',
        description: 'Canvas not ready. Please try again.',
        variant: 'destructive'
      });
      return;
    }

    setIsEnhancing(true);
    
    try {
      // Get the canvas with lighting effects
      const canvas = presentationCanvasRef.current.getCanvas();
      if (!canvas) {
        throw new Error('Could not access canvas');
      }

      // Convert canvas to base64
      const imageBase64 = canvas.toDataURL('image/png');
      
      toast({
        title: 'Enhancing...',
        description: 'AI is adding hyper-realistic effects. This may take 15-30 seconds.',
      });

      const response = await authFetch('/api/ai-enhance', {
        method: 'POST',
        body: JSON.stringify({
          image_base64: imageBase64,
          strength: aiStrength
        })
      });

      if (response.status === 402) {
        // Upgrade required
        const errBody = await readJSON(response);
        const msg = errBody?.detail?.message || 'AI Enhance is a Pro feature.';
        setUpgradeReason('AI Enhance');
        setShowUpgradeModal(true);
        toast({
          title: 'Upgrade to Pro',
          description: msg,
          variant: 'destructive',
          duration: 6000,
        });
        return;
      }

      if (response.status === 401) {
        toast({
          title: 'Session expired',
          description: 'Please sign in again.',
          variant: 'destructive',
        });
        navigate('/login');
        return;
      }

      if (!response.ok) {
        const errBody = await readJSON(response);
        const serverMsg = errBody.message || (typeof errBody.detail === 'string' ? errBody.detail : errBody.detail?.message) || '';
        throw new Error(
          serverMsg
            ? `AI enhance failed (${response.status}): ${serverMsg}`
            : `AI enhance failed (${response.status} ${response.statusText || 'error'})`
        );
      }

      const result = await readJSON(response);

      if (result.success && result.enhanced_image_base64) {
        setEnhancedImage(result.enhanced_image_base64);
        toast({
          title: 'Enhancement Complete!',
          description: 'AI has added realistic light bloom, shadows, and reflections.',
        });
      } else {
        throw new Error(result.message || 'Enhancement failed');
      }
    } catch (error) {
      console.error('AI Enhancement error:', error);
      toast({
        title: 'Enhancement Failed',
        description: error.message || 'Could not enhance image. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsEnhancing(false);
    }
  };

  // Download enhanced image
  const handleDownloadEnhanced = () => {
    if (!enhancedImage) return;
    
    const link = document.createElement('a');
    link.href = enhancedImage;
    link.download = `facade-lighting-enhanced-${Date.now()}.png`;
    link.click();
    
    toast({
      title: 'Downloaded',
      description: 'AI-enhanced image saved.',
    });
  };

  const getColorTempLabel = (temp) => {
    switch(temp) {
      case 2700: return '2700K';
      case 3000: return '3000K';
      case 4000: return '4000K';
      default: return '2700K';
    }
  };

  const handleColorTempChange = (temp) => {
    updateSelectedLight({ colorTemp: temp, customColor: null });
  };

  const handleCustomColorChange = (color) => {
    updateSelectedLight({ customColor: color, colorTemp: null });
  };

  const getAmbientLabel = () => {
    if (ambientDarkness < 20) return 'Daylight';
    if (ambientDarkness < 50) return 'Evening';
    if (ambientDarkness < 80) return 'Dusk';
    return 'Night';
  };

  // Load recent projects from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('recentProjects');
    if (saved) {
      try {
        setRecentProjects(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load recent projects:', e);
      }
    }
  }, []);

  // Save project to JSON file
  const handleSaveProject = () => {
    if (!uploadedImage) {
      toast({
        title: 'Cannot save',
        description: 'Please upload an image first',
        variant: 'destructive'
      });
      return;
    }

    const projectData = {
      version: '1.0',
      timestamp: new Date().toISOString(),
      image: uploadedImage,
      lights: lights,
      ambientDarkness: ambientDarkness,
      selectedLightId: selectedLightId,
      // Include groups data
      groups: groups,
      selectedGroupId: selectedGroupId,
      // Include logos data (array format)
      logos: logos,
      // Include occluders (architectural masks)
      occluders: occluders
    };

    try {
      // Create JSON file
      const dataStr = JSON.stringify(projectData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      // Create filename with date
      const date = new Date().toISOString().split('T')[0];
      const filename = `facade-lighting-${date}.json`;
      
      // Download file
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);

      // Add to recent projects
      addToRecentProjects(projectData, filename);

      toast({
        title: 'Project saved',
        description: `Saved as ${filename}`,
      });
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: 'Save failed',
        description: 'Error creating project file',
        variant: 'destructive'
      });
    }
  };

  // Add project to recent projects list
  const addToRecentProjects = (projectData, filename) => {
    const recentProject = {
      id: Date.now(),
      name: filename,
      timestamp: projectData.timestamp,
      data: projectData
    };

    const updated = [recentProject, ...recentProjects].slice(0, 5); // Keep last 5
    setRecentProjects(updated);
    localStorage.setItem('recentProjects', JSON.stringify(updated));
  };

  // Load project from JSON file
  const handleLoadProject = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const projectData = JSON.parse(e.target.result);
        
        // Validate project data
        if (!projectData.image || !Array.isArray(projectData.lights)) {
          throw new Error('Invalid project file');
        }

        // Restore project state
        setUploadedImage(projectData.image);
        setLights(projectData.lights);
        setAmbientDarkness(projectData.ambientDarkness || 50);
        setSelectedLightId(projectData.selectedLightId || null);

        // Restore groups data if present
        if (projectData.groups && Array.isArray(projectData.groups)) {
          setGroups(projectData.groups);
          setSelectedGroupId(projectData.selectedGroupId || null);
        } else {
          setGroups([]);
          setSelectedGroupId(null);
        }

        // Restore logos data (new array format or legacy single logo)
        if (projectData.logos && Array.isArray(projectData.logos)) {
          setLogos(projectData.logos);
          if (projectData.logos.length > 0) setSelectedLogoId(projectData.logos[0].id);
        } else if (projectData.logo && projectData.logo.image) {
          // Convert legacy single logo to array format
          const legacyLogo = {
            id: Date.now(),
            image: projectData.logo.image,
            position: projectData.logo.position || { x: 50, y: 50 },
            size: projectData.logo.size || 15,
            rotation: projectData.logo.rotation || 0,
            opacity: projectData.logo.opacity || 50,
            tiled: projectData.logo.tiled || false
          };
          setLogos([legacyLogo]);
          setSelectedLogoId(legacyLogo.id);
        } else {
          setLogos([]);
          setSelectedLogoId(null);
        }

        // Restore occluders
        setOccluders(Array.isArray(projectData.occluders) ? projectData.occluders : []);
        setSelectedOccluderId(null);

        // Clear history when loading
        setHistory([]);
        setHistoryIndex(-1);

        // Add to recent projects
        addToRecentProjects(projectData, file.name);

        toast({
          title: 'Project loaded',
          description: `Loaded ${file.name}`,
        });
      } catch (error) {
        console.error('Load error:', error);
        toast({
          title: 'Load failed',
          description: 'Invalid project file',
          variant: 'destructive'
        });
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    event.target.value = '';
  };

  // Load from recent projects
  const handleLoadRecent = (project) => {
    setIsSampleProject(false);
    const projectData = project.data;
    
    setUploadedImage(projectData.image);
    setLights(projectData.lights);
    setAmbientDarkness(projectData.ambientDarkness || 50);
    setSelectedLightId(projectData.selectedLightId || null);

    // Restore groups data if present
    if (projectData.groups && Array.isArray(projectData.groups)) {
      setGroups(projectData.groups);
      setSelectedGroupId(projectData.selectedGroupId || null);
    } else {
      setGroups([]);
      setSelectedGroupId(null);
    }

    // Restore logos data (new array format or legacy single logo)
    if (projectData.logos && Array.isArray(projectData.logos)) {
      setLogos(projectData.logos);
      if (projectData.logos.length > 0) setSelectedLogoId(projectData.logos[0].id);
    } else if (projectData.logo && projectData.logo.image) {
      // Convert legacy single logo to array format
      const legacyLogo = {
        id: Date.now(),
        image: projectData.logo.image,
        position: projectData.logo.position || { x: 50, y: 50 },
        size: projectData.logo.size || 15,
        rotation: projectData.logo.rotation || 0,
        opacity: projectData.logo.opacity || 50,
        tiled: projectData.logo.tiled || false
      };
      setLogos([legacyLogo]);
      setSelectedLogoId(legacyLogo.id);
    } else {
      setLogos([]);
      setSelectedLogoId(null);
    }

    // Restore occluders
    setOccluders(Array.isArray(projectData.occluders) ? projectData.occluders : []);
    setSelectedOccluderId(null);

    // Clear history when loading
    setHistory([]);
    setHistoryIndex(-1);

    setShowRecentProjects(false);

    toast({
      title: 'Project loaded',
      description: `Loaded ${project.name}`,
    });
  };

  // Delete from recent projects
  const handleDeleteRecent = (projectId) => {
    const updated = recentProjects.filter(p => p.id !== projectId);
    setRecentProjects(updated);
    localStorage.setItem('recentProjects', JSON.stringify(updated));
    
    toast({
      title: 'Removed',
      description: 'Project removed from recent list',
    });
  };

  // -------- Sample Project (admin-managed demo) --------
  // Probe sample availability on mount so we can hide the button when none exists.
  useEffect(() => {
    let cancelled = false;
    authFetch('/api/sample-project')
      .then(readJSON)
      .then((data) => {
        if (!cancelled) setHasSample(!!data.available);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleLoadSample = async () => {
    setSampleLoading(true);
    try {
      const res = await authFetch('/api/sample-project');
      if (!res.ok) throw new Error('Could not load sample');
      const data = await readJSON(res);
      if (!data.available || !data.image) {
        toast({
          title: 'Sample not available yet',
          description: 'The owner has not set a sample project yet.',
          variant: 'destructive',
        });
        return;
      }
      setIsSampleProject(true);
      setUploadedImage(data.image);
      setLights(Array.isArray(data.lights) ? data.lights : []);
      setGroups(Array.isArray(data.groups) ? data.groups : []);
      setLogos(Array.isArray(data.logos) ? data.logos : []);
      setOccluders(Array.isArray(data.occluders) ? data.occluders : []);
      setAmbientDarkness(typeof data.ambient_darkness === 'number' ? data.ambient_darkness : 70);
      setSelectedLightId(null);
      setSelectedGroupId(null);
      setSelectedLogoId(null);
      setSelectedOccluderId(null);
      setHistory([]);
      setHistoryIndex(-1);
      toast({
        title: 'Sample Project loaded',
        description: 'Explore freely — your changes are local only.',
      });
    } catch (e) {
      toast({
        title: 'Could not load sample',
        description: e.message || '',
        variant: 'destructive',
      });
    } finally {
      setSampleLoading(false);
    }
  };

  // Admin-only — capture current studio state as the public Sample Project.
  const handleSaveAsSample = async () => {
    if (!isAdmin) return;
    if (!uploadedImage) {
      toast({ title: 'No image to save', variant: 'destructive' });
      return;
    }
    if (!window.confirm('Replace the public Sample Project with the current design? Anyone visiting the site can load this.')) {
      return;
    }
    try {
      const payload = {
        image: uploadedImage,
        lights,
        groups,
        logos,
        occluders,
        ambient_darkness: ambientDarkness,
      };
      const res = await authFetch('/api/admin/sample-project', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await readJSON(res);
        throw new Error(err?.detail || 'Save failed');
      }
      setHasSample(true);
      toast({
        title: 'Sample Project updated',
        description: 'New visitors will see this design when they click Sample Project.',
      });
    } catch (e) {
      toast({
        title: 'Could not save sample',
        description: e.message || '',
        variant: 'destructive',
      });
    }
  };

  // Logo/Watermark handlers
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const newLogo = {
        id: Date.now(),
        image: e.target.result,
        position: { x: 50, y: 50 },
        size: 15,
        rotation: 0,
        opacity: 50,
        tiled: false
      };
      setLogos(prev => [...prev, newLogo]);
      setSelectedLogoId(newLogo.id);
      toast({
        title: 'Logo added',
        description: 'Drag on canvas to reposition',
      });
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const handleRemoveLogo = () => {
    if (!selectedLogoId) return;
    setLogos(prev => prev.filter(l => l.id !== selectedLogoId));
    setSelectedLogoId(logos.length > 1 ? logos.find(l => l.id !== selectedLogoId)?.id : null);
    toast({
      title: 'Logo removed',
    });
  };

  // Update selected logo properties
  const updateSelectedLogo = (updates) => {
    if (!selectedLogoId) return;
    setLogos(prev => prev.map(logo => 
      logo.id === selectedLogoId ? { ...logo, ...updates } : logo
    ));
  };

  // Toggle selection mode for grouping
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedLights([]);
    if (!selectionMode) {
      toast({
        title: 'Selection mode',
        description: 'Click lights to select them for grouping or deletion',
      });
    }
  };

  // Toggle between list and grid view for the lights list
  const toggleLightsViewMode = () => {
    setLightsViewMode((prev) => {
      const next = prev === 'list' ? 'grid' : 'list';
      try { localStorage.setItem('fls_lights_view_mode', next); } catch (_) {}
      return next;
    });
  };

  // Reorder lights via drag-and-drop (grid view)
  const [draggedLightIdx, setDraggedLightIdx] = useState(null);
  const [dragOverLightIdx, setDragOverLightIdx] = useState(null);

  // Animate Timeline — plays lights ON one-by-one for screen recording
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationVisibleCount, setAnimationVisibleCount] = useState(0);
  const [animationSpeed, setAnimationSpeed] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('fls_anim_speed_ms') || '400', 10);
      return [200, 400, 800].includes(v) ? v : 400;
    } catch { return 400; }
  });
  const [animationLoop, setAnimationLoop] = useState(() => {
    try { return localStorage.getItem('fls_anim_loop') === '1'; } catch { return false; }
  });
  const animationTimerRef = useRef(null);
  const animationLoopRef = useRef(false);
  useEffect(() => { animationLoopRef.current = animationLoop; }, [animationLoop]);

  const stopAnimation = () => {
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
      animationTimerRef.current = null;
    }
    setIsAnimating(false);
    setAnimationVisibleCount(0);
  };

  const startAnimation = () => {
    if (lights.length === 0) {
      toast({
        title: 'No lights to animate',
        description: 'Add at least one light before playing the timeline.',
        variant: 'destructive',
      });
      return;
    }
    if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
    setSelectedLightId(null);
    setIsAnimating(true);
    setAnimationVisibleCount(0);

    const STEP_MS = animationSpeed;
    const HOLD_MS = 1200;
    const total = lights.length;

    const step = (i) => {
      setAnimationVisibleCount(i);
      if (i < total) {
        animationTimerRef.current = setTimeout(() => step(i + 1), STEP_MS);
      } else {
        animationTimerRef.current = setTimeout(() => {
          if (animationLoopRef.current) {
            // Restart from 0 after hold
            setAnimationVisibleCount(0);
            animationTimerRef.current = setTimeout(() => step(1), STEP_MS);
          } else {
            setIsAnimating(false);
            setAnimationVisibleCount(0);
            animationTimerRef.current = null;
          }
        }, HOLD_MS);
      }
    };
    animationTimerRef.current = setTimeout(() => step(1), STEP_MS);
  };

  const setAnimationSpeedPersisted = (ms) => {
    setAnimationSpeed(ms);
    try { localStorage.setItem('fls_anim_speed_ms', String(ms)); } catch (_) {}
  };

  const toggleAnimationLoop = () => {
    setAnimationLoop((prev) => {
      const next = !prev;
      try { localStorage.setItem('fls_anim_loop', next ? '1' : '0'); } catch (_) {}
      return next;
    });
  };

  // Cleanup animation timer on unmount
  useEffect(() => () => {
    if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
  }, []);

  const reorderLights = (fromIdx, toIdx) => {
    if (fromIdx == null || toIdx == null || fromIdx === toIdx) return;
    setLights((prev) => {
      if (fromIdx < 0 || fromIdx >= prev.length) return prev;
      if (toIdx < 0 || toIdx >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  // Delete all currently-selected lights in selection mode
  const handleDeleteSelectedLights = () => {    if (selectedLights.length === 0) return;
    const ids = new Set(selectedLights);
    const count = ids.size;

    // Remove the lights themselves
    setLights(lights.filter((l) => !ids.has(l.id)));

    // Clean groups: drop these ids from each group, remove empty groups
    const updatedGroups = groups
      .map((g) => ({ ...g, lightIds: g.lightIds.filter((id) => !ids.has(id)) }))
      .filter((g) => g.lightIds.length > 0);
    setGroups(updatedGroups);

    // Clear any dangling selections
    if (selectedLightId && ids.has(selectedLightId)) setSelectedLightId(null);
    setSelectedLights([]);
    setSelectionMode(false);

    toast({
      title: `${count} light${count === 1 ? '' : 's'} deleted`,
      description: 'Removed from canvas and any groups they belonged to',
    });
  };

  // Toggle light selection
  const toggleLightSelection = (lightId) => {
    if (selectedLights.includes(lightId)) {
      setSelectedLights(selectedLights.filter(id => id !== lightId));
    } else {
      setSelectedLights([...selectedLights, lightId]);
    }
  };

  // Create group from selected lights
  const handleCreateGroup = () => {
    if (selectedLights.length < 2) {
      toast({
        title: 'Cannot create group',
        description: 'Select at least 2 lights to create a group',
        variant: 'destructive'
      });
      return;
    }

    const newGroup = {
      id: Date.now(),
      name: `Group ${groups.length + 1}`,
      lightIds: selectedLights
    };

    setGroups([...groups, newGroup]);
    setSelectedLights([]);
    setSelectionMode(false);

    toast({
      title: 'Group created',
      description: `Created ${newGroup.name} with ${selectedLights.length} lights`,
    });
  };

  // Delete group
  const handleDeleteGroup = (groupId) => {
    setGroups(groups.filter(g => g.id !== groupId));
    if (selectedGroupId === groupId) {
      setSelectedGroupId(null);
    }
    toast({
      title: 'Group deleted',
    });
  };

  // Rename group
  const handleRenameGroup = (groupId, newName) => {
    setGroups(groups.map(g => 
      g.id === groupId ? { ...g, name: newName } : g
    ));
  };

  // Select group (select all lights in group)
  const handleSelectGroup = (groupId) => {
    const group = groups.find(g => g.id === groupId);
    if (group) {
      setSelectedGroupId(groupId);
      // Select first light in group for display
      if (group.lightIds.length > 0) {
        setSelectedLightId(group.lightIds[0]);
      }
    }
  };

  // Move entire group (maintaining relative positions)
  const handleGroupDrag = (groupId, deltaX, deltaY) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;

    const updatedLights = lights.map(light => {
      if (group.lightIds.includes(light.id)) {
        return {
          ...light,
          position: {
            x: Math.max(0, Math.min(100, light.position.x + deltaX)),
            y: Math.max(0, Math.min(100, light.position.y + deltaY))
          }
        };
      }
      return light;
    });

    setLights(updatedLights);
  };

  // Batch Export - export multiple ambient levels
  // Helper function to render canvas with specific ambient darkness
  const renderCanvasForExport = (targetAmbient) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current?.getCanvas();
      if (!canvas) {
        resolve(null);
        return;
      }
      
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.src = uploadedImage;
      
      img.onload = () => {
        // Clear and draw base image
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Apply ambient darkness overlay
        ctx.fillStyle = `rgba(0, 0, 0, ${targetAmbient})`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Re-draw all lights on top
        lights.forEach(light => {
          canvasRef.current?.drawLightForExport?.(ctx, canvas, light);
        });
        
        // Draw logo if present
        if (logoImage) {
          canvasRef.current?.drawLogoForExport?.(ctx, canvas, {
            image: logoImage,
            position: logoPosition,
            size: logoSize,
            rotation: logoRotation,
            opacity: logoOpacity,
            tiled: logoTiled
          });
        }
        
        // Capture after a frame to ensure drawing is complete
        requestAnimationFrame(() => {
          const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
          resolve(dataUrl);
        });
      };
      
      img.onerror = () => resolve(null);
    });
  };

  const handleBatchExport = async () => {
    // Server-side quota check first (free users: 3 exports / month; batch counts as one).
    const allowedBatch = await checkExportQuota('batch');
    if (!allowedBatch) return;

    const exportLevels = [
      { name: 'daylight', label: 'Daylight', ambient: 0.25 },    // 75% light (25% darkness)
      { name: 'evening', label: 'Evening', ambient: 0.6 },       // 40% light (60% darkness)
      { name: 'night', label: 'Night', ambient: 0.85 }           // 15% light (85% darkness)
    ];
    
    const originalAmbient = ambientDarkness;
    const originalSelected = selectedLightId;
    
    // Deselect all lights and enable preview mode
    setSelectedLightId(null);
    const wasInPreview = previewMode;
    setPreviewMode(true);
    
    // Wait for preview mode and deselection to take effect
    await new Promise(resolve => setTimeout(resolve, 300));
    
    toast({
      title: 'Creating PDF export',
      description: 'Capturing 3 lighting modes...',
    });
    
    // Array to store image data URLs
    const imageDataUrls = [];
    
    for (let i = 0; i < exportLevels.length; i++) {
      const level = exportLevels[i];
      
      // Set ambient darkness and wait for React re-render
      setAmbientDarkness(level.ambient * 100); // Convert to 0-100 scale
      
      // Wait for state update and canvas re-render
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Force a redraw by triggering the canvas ref
      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Get canvas and capture as data URL
      const canvas = canvasRef.current?.getCanvas();
      if (canvas) {
        const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
        imageDataUrls.push({
          dataUrl,
          label: level.label,
          ambient: level.ambient
        });
        
        toast({
          title: `Captured ${level.label}`,
          description: `${i + 1} of ${exportLevels.length} complete`,
        });
      }
      
      // Small delay between captures
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Restore preview mode state
    setPreviewMode(wasInPreview);
    
    // Restore original ambient and selection
    setAmbientDarkness(originalAmbient);
    setSelectedLightId(originalSelected);
    
    // Create PDF with all images
    if (imageDataUrls.length === 3) {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const imageWidth = pageWidth - (2 * margin);
      
      // Calculate image height maintaining aspect ratio
      const canvas = canvasRef.current.getCanvas();
      const aspectRatio = canvas.height / canvas.width;
      const imageHeight = imageWidth * aspectRatio;
      
      // Add each image to PDF
      imageDataUrls.forEach((item, index) => {
        if (index > 0) {
          pdf.addPage();
        }
        
        // Add title
        pdf.setFontSize(16);
        pdf.setTextColor(0, 0, 0);
        pdf.text(item.label + ' View', pageWidth / 2, 15, { align: 'center' });
        
        // Add ambient info
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        const lightPercent = Math.round((1 - item.ambient) * 100);
        pdf.text(`${lightPercent}% Light Level`, pageWidth / 2, 22, { align: 'center' });
        
        // Add image
        pdf.addImage(
          item.dataUrl,
          'JPEG',
          margin,
          28,
          imageWidth,
          Math.min(imageHeight, pageHeight - 38)
        );
        
        // Add footer with date
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        const date = new Date().toLocaleDateString();
        pdf.text(`Facade Lighting Studio - Generated on ${date}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
      });
      
      // Save PDF
      const filename = `facade-lighting-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
      
      toast({
        title: 'PDF exported successfully',
        description: 'All 3 lighting modes saved in PDF',
      });
    } else {
      toast({
        title: 'Export failed',
        description: 'Could not capture all images',
        variant: 'destructive'
      });
    }
  };

  // Sidebar resize handlers
  const handleResizeStart = (e) => {
    setIsResizing(true);
    resizeStartX.current = e.clientX || e.touches[0].clientX;
    resizeStartWidth.current = sidebarWidth;
  };

  const handleResizeMove = (e) => {
    if (!isResizing) return;
    
    const clientX = e.clientX || e.touches[0]?.clientX;
    if (!clientX) return;
    
    const containerWidth = window.innerWidth;
    const deltaX = resizeStartX.current - clientX;
    const deltaPercent = (deltaX / containerWidth) * 100;
    let newWidth = resizeStartWidth.current + deltaPercent;
    
    // Apply min/max constraints
    newWidth = Math.max(10, Math.min(70, newWidth));
    
    // Snap to points (within 2% threshold)
    const snapPoints = [10, 25, 50, 70];
    for (const snapPoint of snapPoints) {
      if (Math.abs(newWidth - snapPoint) < 2) {
        newWidth = snapPoint;
        break;
      }
    }
    
    setSidebarWidth(newWidth);
  };

  const handleResizeEnd = () => {
    if (isResizing) {
      setIsResizing(false);
      localStorage.setItem('sidebarWidth', sidebarWidth.toString());
    }
  };

  // Add event listeners for resize
  useEffect(() => {
    if (isResizing) {
      const handleMouseMove = (e) => handleResizeMove(e);
      const handleMouseUp = () => handleResizeEnd();
      const handleTouchMove = (e) => handleResizeMove(e);
      const handleTouchEnd = () => handleResizeEnd();
      
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isResizing, sidebarWidth]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        reason={upgradeReason}
      />
      <FeedbackWidget
        open={showFeedback}
        onOpenChange={setShowFeedback}
        context={feedbackContext}
      />
      {/* Presentation Mode - Full Screen Comparison View */}
      {presentationMode && uploadedImage && (
        <div className="fixed inset-0 z-[100] bg-black flex flex-col">
          {/* Top Bar */}
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-20">
            <div className="flex items-center gap-3">
              <div className="bg-amber-500/20 backdrop-blur-md border border-amber-500/50 rounded-lg px-4 py-2">
                <span className="text-amber-400 font-semibold flex items-center gap-2">
                  <Presentation className="w-5 h-5" />
                  Presentation Mode
                </span>
              </div>
              <div className="bg-slate-800/80 backdrop-blur-md border border-slate-600/50 rounded-lg px-3 py-2 text-sm text-slate-300">
                Drag the slider to compare Before & After
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* AI Enhancement Button */}
              <Button
                onClick={handleAIEnhance}
                disabled={isEnhancing}
                className="bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white shadow-xl disabled:opacity-50"
              >
                {isEnhancing ? (
                  <>
                    <svg className="animate-spin w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enhancing...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                      <path d="M2 17l10 5 10-5"/>
                      <path d="M2 12l10 5 10-5"/>
                    </svg>
                    Finalize with AI
                  </>
                )}
              </Button>
              
              {/* Download Enhanced Image (if available) */}
              {enhancedImage && (
                <Button
                  onClick={handleDownloadEnhanced}
                  className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white shadow-xl"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Enhanced
                </Button>
              )}
              
              <Button
                onClick={handleExport}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-xl"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              <Button
                onClick={() => {
                  setPresentationMode(false);
                  setEnhancedImage(null); // Clear enhanced image when exiting
                }}
                className="bg-slate-800/80 backdrop-blur-md hover:bg-slate-700/80 text-white shadow-xl"
              >
                <X className="w-4 h-4 mr-2" />
                Exit
              </Button>
            </div>
          </div>
          
          {/* Enhanced Image Preview Overlay */}
          {enhancedImage && (
            <div className="absolute inset-0 z-30 bg-black/90 flex flex-col items-center justify-center p-8">
              <div className="text-center mb-4">
                <h2 className="text-2xl font-bold text-white mb-2">AI-Enhanced Result</h2>
                <p className="text-slate-400">Hyper-realistic light bloom, shadows, and reflections added</p>
              </div>
              <div className="relative max-w-full max-h-[70vh] rounded-xl overflow-hidden shadow-2xl border-2 border-purple-500/50">
                <img 
                  src={enhancedImage} 
                  alt="AI Enhanced" 
                  className="max-w-full max-h-[70vh] object-contain"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={handleDownloadEnhanced}
                  className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white shadow-xl px-6"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Enhanced Image
                </Button>
                <Button
                  onClick={() => setEnhancedImage(null)}
                  className="bg-slate-700 hover:bg-slate-600 text-white shadow-xl"
                >
                  Back to Comparison
                </Button>
              </div>
            </div>
          )}
          
          {/* Comparison Container */}
          <div className="flex-1 relative overflow-hidden">
            {/* Original Image with same ambient darkness (Left side - visible through clip) */}
            <div 
              className="absolute inset-0"
              style={{ 
                clipPath: `inset(0 ${100 - comparisonSliderPos}% 0 0)` 
              }}
            >
              <LightingCanvas
                ref={originalCanvasRef}
                image={uploadedImage}
                lights={[]} // No lights - just ambient darkness
                ambientDarkness={ambientDarkness}
                onLightDrag={() => {}}
                onGrazerDrag={() => {}}
                onLightSelect={() => {}}
                selectedLightId={null}
                previewMode={true}
                logos={[]} // No logos on before side
                selectedLogoId={null}
                onLogoSelect={() => {}}
                onLogoPositionChange={() => {}}
                isDrawingNeon={false}
                onNeonDrawingComplete={() => {}}
                zoomLevel={1}
                panOffset={{ x: 0, y: 0 }}
                occluders={occluders}
              />
              {/* Label */}
              <div className="absolute bottom-8 left-8 bg-black/70 backdrop-blur-md rounded-lg px-4 py-2">
                <span className="text-white font-semibold">BEFORE</span>
              </div>
            </div>
            
            {/* Edited Canvas (Right side - visible through clip) */}
            <div 
              className="absolute inset-0"
              style={{ 
                clipPath: `inset(0 0 0 ${comparisonSliderPos}%)` 
              }}
            >
              <LightingCanvas
                ref={presentationCanvasRef}
                image={uploadedImage}
                lights={lights.filter(light => {
                  if (light.beamType === 'grazer' || light.beamType === 'neon') {
                    const groupContainingLight = groups.find(g => g.lightIds.includes(light.id));
                    if (groupContainingLight && hiddenGroups.includes(groupContainingLight.id)) {
                      return false;
                    }
                    return true;
                  }
                  const groupContainingLight = groups.find(g => g.lightIds.includes(light.id));
                  if (groupContainingLight && hiddenGroups.includes(groupContainingLight.id)) {
                    return false;
                  }
                  return true;
                })}
                ambientDarkness={ambientDarkness}
                onLightDrag={() => {}}
                onGrazerDrag={() => {}}
                onLightSelect={() => {}}
                selectedLightId={null}
                previewMode={true}
                logos={logos}
                selectedLogoId={null}
                onLogoSelect={() => {}}
                onLogoPositionChange={() => {}}
                isDrawingNeon={false}
                onNeonDrawingComplete={() => {}}
                zoomLevel={1}
                panOffset={{ x: 0, y: 0 }}
                occluders={occluders}
              />
              {/* Label */}
              <div className="absolute bottom-8 right-8 bg-amber-500/80 backdrop-blur-md rounded-lg px-4 py-2">
                <span className="text-white font-semibold">AFTER</span>
              </div>
            </div>
            
            {/* Vertical Slider Handle */}
            <div 
              className="absolute top-0 bottom-0 w-1 bg-white shadow-2xl cursor-ew-resize z-10"
              style={{ left: `${comparisonSliderPos}%`, transform: 'translateX(-50%)' }}
              onMouseDown={(e) => {
                e.preventDefault();
                setIsDraggingSlider(true);
              }}
              onTouchStart={(e) => {
                setIsDraggingSlider(true);
              }}
            >
              {/* Handle Grip */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-16 bg-white rounded-lg shadow-2xl flex items-center justify-center cursor-ew-resize border-2 border-slate-300">
                <GripHorizontal className="w-6 h-6 text-slate-500 rotate-90" />
              </div>
            </div>
          </div>
          
          {/* Mouse/Touch move handlers for slider */}
          {isDraggingSlider && (
            <div 
              className="fixed inset-0 z-50 cursor-ew-resize"
              onMouseMove={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const percentage = Math.max(5, Math.min(95, (x / rect.width) * 100));
                setComparisonSliderPos(percentage);
              }}
              onMouseUp={() => setIsDraggingSlider(false)}
              onMouseLeave={() => setIsDraggingSlider(false)}
              onTouchMove={(e) => {
                const touch = e.touches[0];
                const rect = e.currentTarget.getBoundingClientRect();
                const x = touch.clientX - rect.left;
                const percentage = Math.max(5, Math.min(95, (x / rect.width) * 100));
                setComparisonSliderPos(percentage);
              }}
              onTouchEnd={() => setIsDraggingSlider(false)}
            />
          )}
        </div>
      )}
      
      {!uploadedImage ? (
        <div className="h-screen flex items-center justify-center relative">
          {/* Account widget — top-right on the project picker */}
          <div className="absolute top-4 right-4 z-10">
            {!signedIn ? (
              <Button
                onClick={() => navigate('/login')}
                size="sm"
                data-testid="topbar-login-btn"
                className="bg-slate-800/60 backdrop-blur-md hover:bg-slate-700/70 text-white border border-slate-600/50 shadow-lg"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign in
              </Button>
            ) : (
              <div className="flex items-center gap-1 bg-slate-800/60 backdrop-blur-md border border-slate-600/50 rounded-lg p-1 shadow-lg" data-testid="topbar-account-pill">
                <div className="flex items-center gap-2 px-2 py-1 text-xs">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-5 h-5 rounded-full" />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-[10px] font-bold text-white">
                      {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="text-slate-300 max-w-[140px] truncate hidden sm:inline">
                    {user?.name || user?.email}
                  </span>
                  {isPro ? (
                    <span className="text-[10px] uppercase tracking-wider text-violet-300 bg-violet-500/20 border border-violet-500/30 px-1.5 py-0.5 rounded">
                      <Crown className="w-3 h-3 inline mr-0.5" />Pro
                    </span>
                  ) : (
                    <button
                      onClick={() => { setUpgradeReason('Pro features'); setShowUpgradeModal(true); }}
                      data-testid="topbar-free-upgrade-pill"
                      className="text-[10px] uppercase tracking-wider text-amber-300 bg-amber-500/20 border border-amber-500/30 hover:bg-amber-500/30 px-1.5 py-0.5 rounded transition"
                      title="Upgrade to Pro"
                    >
                      Free · Upgrade
                    </button>
                  )}
                </div>
                {isAdmin && (
                  <Button
                    onClick={() => navigate('/admin')}
                    size="sm"
                    data-testid="topbar-admin-btn"
                    className="bg-transparent hover:bg-slate-700/70 text-amber-400 border-0 h-7 px-2"
                    title="Owner dashboard"
                  >
                    <Shield className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Button
                  onClick={async () => { await logout(); navigate('/login'); }}
                  size="sm"
                  data-testid="topbar-logout-btn"
                  className="bg-transparent hover:bg-slate-700/70 text-slate-400 hover:text-white border-0 h-7 px-2"
                  title="Sign out"
                >
                  <LogOut className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>

          <div className="text-center space-y-6 max-w-2xl">
            <BrandLogo className="w-64 sm:w-72 mx-auto" />
            <p className="text-slate-400 text-lg">Design stunning architectural lighting effects</p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
              {/* New Project */}
              <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:border-cyan-500/50 transition-all">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
                    <Upload className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">New Project</h3>
                    <p className="text-slate-400 text-sm mb-4">Start fresh with a new building photo</p>
                  </div>
                  <Button
                    onClick={() => { setIsSampleProject(false); fileInputRef.current?.click(); }}
                    data-testid="picker-new-project-btn"
                    className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white py-3"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Photo
                  </Button>
                </div>
              </div>

              {/* Sample Project — admin-managed demo */}
              <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:border-amber-500/50 transition-all">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Sample Project</h3>
                    <p className="text-slate-400 text-sm mb-4">
                      {hasSample ? 'Try our preset design — no upload needed' : 'Coming soon — owner hasn\'t set a demo yet'}
                    </p>
                  </div>
                  <Button
                    onClick={handleLoadSample}
                    disabled={!hasSample || sampleLoading}
                    data-testid="picker-sample-project-btn"
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {sampleLoading ? 'Loading…' : 'Try Sample'}
                  </Button>
                </div>
              </div>

              {/* Open Existing Project */}
              <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:border-emerald-500/50 transition-all">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                    <FolderOpen className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Open Project</h3>
                    <p className="text-slate-400 text-sm mb-4">Continue working on saved project</p>
                  </div>
                  <Button
                    onClick={() => { setIsSampleProject(false); loadProjectInputRef.current?.click(); }}
                    data-testid="picker-open-project-btn"
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white py-3"
                  >
                    <FolderOpen className="w-4 h-4 mr-2" />
                    Load Project
                  </Button>
                </div>
              </div>
            </div>

            {/* Recent Projects */}
            {recentProjects.length > 0 && (
              <div className="mt-8">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Recent Projects</h3>
                <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                  {recentProjects.map((project) => (
                    <div
                      key={project.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleLoadRecent(project)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleLoadRecent(project); } }}
                      className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-lg p-4 hover:border-amber-500/50 transition-all text-left group flex items-center justify-between cursor-pointer"
                      data-testid={`recent-project-${project.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Clock className="w-4 h-4 text-amber-400" />
                        <div>
                          <div className="text-sm text-white font-medium">{project.name}</div>
                          <div className="text-xs text-slate-400">
                            {new Date(project.timestamp).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRecent(project.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 p-2 hover:bg-red-500/20 rounded transition-all"
                        data-testid={`delete-recent-project-${project.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Legal links — Razorpay/Stripe/Google compliance */}
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4 text-[11px] text-slate-500" data-testid="picker-legal-footer">
            <Link to="/about" className="hover:text-amber-400 transition">About</Link>
            <Link to="/pricing" className="hover:text-amber-400 transition">Pricing</Link>
            <Link to="/privacy" className="hover:text-amber-400 transition">Privacy</Link>
            <Link to="/terms" className="hover:text-amber-400 transition">Terms</Link>
            <Link to="/refund" className="hover:text-amber-400 transition">Refund</Link>
            <Link to="/contact" className="hover:text-amber-400 transition">Contact</Link>
          </div>
        </div>
      ) : (
        <div className="flex gap-4 h-[calc(100vh-2rem)]">
          {/* Main Canvas - Takes most space */}
          <div 
            className="flex-1 bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-2xl overflow-auto relative"
            onWheel={handleWheelZoom}
          >
            {/* Sample Project footer pill — visible on the canvas while exploring the demo */}
            {isSampleProject && (
              <div
                data-testid="sample-project-footer"
                className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-amber-400/40 shadow-lg pointer-events-none"
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs font-semibold text-amber-200">
                  Sample Project
                </span>
                <span className="text-xs text-slate-400 hidden sm:inline">·</span>
                <span className="text-xs text-slate-300 hidden sm:inline">
                  Facade Lighting Studio
                </span>
              </div>
            )}
            <LightingCanvas
              ref={canvasRef}
              image={uploadedImage}
              lights={lights.filter((light, idx) => {
                // Animate Timeline: only show first N lights while animating
                if (isAnimating && idx >= animationVisibleCount) return false;
                // Check if this light belongs to any hidden group
                const lightGroup = groups.find(g => g.lightIds.includes(light.id));
                if (lightGroup && hiddenGroups.includes(lightGroup.id)) {
                  return false; // Hide this light
                }
                return true; // Show this light
              })}
              ambientDarkness={ambientDarkness}
              onLightDrag={handleLightDrag}
              onGrazerDrag={handleGrazerDrag}
              onLightSelect={setSelectedLightId}
              selectedLightId={selectedLightId}
              previewMode={previewMode}
              logos={logos}
              selectedLogoId={selectedLogoId}
              onLogoSelect={setSelectedLogoId}
              onLogoPositionChange={(logoId, newPosition) => {
                setLogos(prev => prev.map(logo => 
                  logo.id === logoId ? { ...logo, position: newPosition } : logo
                ));
              }}
              isDrawingNeon={isDrawingNeon}
              onNeonDrawingComplete={handleNeonDrawingComplete}
              selectedNeonPointIndex={selectedNeonPointIndex}
              onNeonPointSelect={setSelectedNeonPointIndex}
              onNeonPathUpdate={(lightId, newPath) => {
                setLights(lights.map(light => 
                  light.id === lightId ? { ...light, path: newPath } : light
                ));
              }}
              zoomLevel={zoomLevel}
              panOffset={panOffset}
              onPanChange={setPanOffset}
              isPanning={isPanning}
              setIsPanning={setIsPanning}
              panStartRef={panStartRef}
              lastPanOffsetRef={lastPanOffsetRef}
              occluders={occluders}
              selectedOccluderId={selectedOccluderId}
              onOccluderSelect={(id) => { setSelectedOccluderId(id); if (id) setSelectedLightId(null); }}
              onOccluderPositionChange={handleOccluderPositionChange}
              onOccluderResize={handleOccluderResize}
              onNeonResize={handleNeonResize}
              isDrawingOccluder={isDrawingOccluder}
              onOccluderDrawingComplete={handleOccluderDrawingComplete}
              fitMode={fitMode}
              customWidth={customWidth}
              customHeight={customHeight}
            />
            
            {/* Zoom Controls - Bottom Left */}
            {!previewMode && (
              <div className="absolute bottom-4 left-4 flex flex-col items-start gap-2 z-10">
                <div className="flex items-center gap-1 bg-slate-800/80 backdrop-blur-md border border-slate-600/50 rounded-lg p-1">
                  <Button
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 1}
                    size="sm"
                    className="bg-transparent hover:bg-slate-700/70 text-white disabled:opacity-30 disabled:cursor-not-allowed border-0 px-2"
                    title="Zoom Out"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      <line x1="8" y1="11" x2="14" y2="11"/>
                    </svg>
                  </Button>
                  <span className="text-white text-xs font-medium px-2 min-w-[50px] text-center">
                    {Math.round(zoomLevel * 100)}%
                  </span>
                  <Button
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= 4}
                    size="sm"
                    className="bg-transparent hover:bg-slate-700/70 text-white disabled:opacity-30 disabled:cursor-not-allowed border-0 px-2"
                    title="Zoom In"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      <line x1="11" y1="8" x2="11" y2="14"/>
                      <line x1="8" y1="11" x2="14" y2="11"/>
                    </svg>
                  </Button>
                  {zoomLevel > 1 && (
                    <Button
                      onClick={handleZoomReset}
                      size="sm"
                      className="bg-transparent hover:bg-slate-700/70 text-white border-0 px-2 ml-1"
                      title="Reset Zoom"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                        <path d="M3 3v5h5"/>
                      </svg>
                    </Button>
                  )}
                </div>
                {/* Fit-mode toggle — Fit (best) / Width / Height / Custom */}
                <div className="flex flex-col gap-1">
                  <div
                    className="flex items-center gap-1 bg-slate-800/80 backdrop-blur-md border border-slate-600/50 rounded-lg px-1.5 py-1"
                    data-testid="fit-mode-toggle"
                  >
                    <span className="text-[10px] text-slate-400 uppercase tracking-wider px-1">Fit</span>
                    {[
                      { v: 'fit', label: 'Best', tip: 'Fit whole image inside canvas' },
                      { v: 'width', label: 'Width', tip: 'Fill canvas width — may scroll vertically' },
                      { v: 'height', label: 'Height', tip: 'Fill canvas height — may leave side gaps' },
                      { v: 'custom', label: 'Custom', tip: 'Set custom canvas dimensions' },
                    ].map(({ v, label, tip }) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => {
                          setFitModePersisted(v);
                          if (v === 'custom') setShowCustomSizeInput(true);
                        }}
                        title={tip}
                        data-testid={`fit-mode-${v}`}
                        className={`text-[11px] px-1.5 py-0.5 rounded ${
                          fitMode === v
                            ? 'bg-cyan-500 text-white'
                            : 'text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  
                  {/* Custom Size Input */}
                  {fitMode === 'custom' && showCustomSizeInput && (
                    <div className="bg-slate-800/90 backdrop-blur-md border border-cyan-500/50 rounded-lg p-2">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] text-cyan-400 uppercase font-semibold">Custom Size</span>
                        <button
                          onClick={() => setShowCustomSizeInput(false)}
                          className="ml-auto text-slate-400 hover:text-white"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={customWidth}
                          onChange={(e) => {
                            const val = Math.max(320, Math.min(3840, parseInt(e.target.value) || 1920));
                            setCustomWidth(val);
                            try { localStorage.setItem('fls_custom_width', val); } catch (_) {}
                          }}
                          className="w-16 px-2 py-1 text-xs bg-slate-700 text-white border border-slate-600 rounded focus:border-cyan-500 focus:outline-none"
                          min="320"
                          max="3840"
                        />
                        <span className="text-xs text-slate-400">×</span>
                        <input
                          type="number"
                          value={customHeight}
                          onChange={(e) => {
                            const val = Math.max(240, Math.min(2160, parseInt(e.target.value) || 1080));
                            setCustomHeight(val);
                            try { localStorage.setItem('fls_custom_height', val); } catch (_) {}
                          }}
                          className="w-16 px-2 py-1 text-xs bg-slate-700 text-white border border-slate-600 rounded focus:border-cyan-500 focus:outline-none"
                          min="240"
                          max="2160"
                        />
                        <span className="text-[10px] text-slate-400">px</span>
                      </div>
                      <div className="text-[9px] text-slate-500 mt-1">
                        Min: 320×240 • Max: 3840×2160
                      </div>
                    </div>
                  )}
                </div>
                {zoomLevel > 1 && (
                  <span className="text-xs text-slate-400 bg-slate-800/60 px-2 py-1 rounded">
                    Hold Space + Drag to pan
                  </span>
                )}
              </div>
            )}
            
            {/* Neon Drawing Mode Indicator */}
            {isDrawingNeon && !previewMode && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
                <div className={`flex items-center gap-3 px-4 py-2 rounded-full border shadow-lg backdrop-blur-md ${
                  isSmartShapeMode 
                    ? 'bg-cyan-500/20 border-cyan-400/50' 
                    : 'bg-pink-500/20 border-pink-400/50'
                }`}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{isSmartShapeMode ? '🔷' : '✨'}</span>
                    <span className="text-sm font-medium text-white">
                      {isSmartShapeMode ? 'Smart Shape Mode' : 'Freeform Neon Mode'}
                    </span>
                  </div>
                  <span className="text-xs text-slate-400">Draw to add</span>
                  <button
                    onClick={exitNeonDrawingMode}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-700/80 hover:bg-slate-600 text-white rounded-full transition-all"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                    Exit (ESC)
                  </button>
                </div>
              </div>
            )}
            
            {/* Occluder Selected Indicator - On-screen ESC button */}
            {selectedOccluderId && !previewMode && !isDrawingNeon && !isDrawingOccluder && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
                <div className="flex items-center gap-3 px-4 py-2 rounded-full border shadow-lg backdrop-blur-md bg-amber-500/20 border-amber-400/50">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🔒</span>
                    <span className="text-sm font-medium text-white">
                      Occluder Selected
                    </span>
                  </div>
                  <span className="text-xs text-slate-400">Drag to move • Corners to resize</span>
                  <button
                    onClick={() => {
                      setSelectedOccluderId(null);
                      toast({ 
                        title: 'Occluder deselected', 
                        description: 'You can now add lights or perform other actions'
                      });
                    }}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-700/80 hover:bg-slate-600 text-white rounded-full transition-all"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                    Deselect (ESC)
                  </button>
                </div>
              </div>
            )}
            
            {!previewMode && (
              <>
                {/* Top Toolbar - More Translucent */}
                <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10">
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Exit — back to the 3-card landing picker */}
                    <Button
                      onClick={() => {
                        if (lights.length > 0 && hasUnsavedChanges) {
                          const ok = window.confirm(
                            'You have unsaved changes. Exit to home and discard them?'
                          );
                          if (!ok) return;
                        }
                        // Reset to the picker state — uploadedImage = null triggers home screen
                        if (animationTimerRef.current) {
                          clearTimeout(animationTimerRef.current);
                          animationTimerRef.current = null;
                        }
                        setIsAnimating(false);
                        setAnimationVisibleCount(0);
                        setLights([]);
                        setGroups([]);
                        setSelectedLightId(null);
                        setSelectedLights([]);
                        setSelectionMode(false);
                        setHiddenGroups([]);
                        setUploadedImage(null);
                        setIsSampleProject(false);
                        setHasUnsavedChanges(false);
                        try { localStorage.removeItem('facadeLightingAutoSave'); } catch (_) {}
                      }}
                      size="sm"
                      data-testid="exit-to-home-btn"
                      className="bg-slate-800/60 backdrop-blur-md hover:bg-slate-700/70 text-white border border-slate-600/50 shadow-lg"
                      title="Exit to home — back to the project picker"
                    >
                      <Home className="w-4 h-4 mr-2" />
                      Exit
                    </Button>
                    <Button
                      onClick={() => {
                        // "New Project" gate — free users get 1 free new-project switch beyond
                        // their initial upload. Pro users (or anonymous on first click) bypass.
                        // Sample Project is exempt — leaving sample doesn't count as a new
                        // project switch (the user hasn't used a "real" slot yet).
                        const FREE_NEW_PROJECT_LIMIT = 1;
                        try {
                          if (!isPro && !isSampleProject) {
                            const used = parseInt(localStorage.getItem('fls_new_project_count_v1') || '0', 10) || 0;
                            if (used >= FREE_NEW_PROJECT_LIMIT) {
                              setUpgradeReason('More projects');
                              setShowUpgradeModal(true);
                              return;
                            }
                            localStorage.setItem('fls_new_project_count_v1', String(used + 1));
                          }
                        } catch (_) {}
                        setIsSampleProject(false);
                        fileInputRef.current?.click();
                      }}
                      size="sm"
                      data-testid="new-project-btn"
                      className="bg-slate-800/60 backdrop-blur-md hover:bg-slate-700/70 text-white border border-slate-600/50 shadow-lg"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      New Project
                    </Button>

                    {/* Admin-only — overwrite the public Sample Project with the current design */}
                    {isAdmin && (
                      <Button
                        onClick={handleSaveAsSample}
                        size="sm"
                        data-testid="save-as-sample-btn"
                        className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 backdrop-blur-md shadow-lg"
                        title="Replace the public Sample Project with the current design"
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Save as Sample
                      </Button>
                    )}

                    {/* Undo/Redo Buttons */}
                    <div className="flex gap-1 bg-slate-800/60 backdrop-blur-md border border-slate-600/50 rounded-lg p-1">
                      <Button
                        onClick={handleUndo}
                        disabled={!canUndo}
                        size="sm"
                        className="bg-transparent hover:bg-slate-700/70 text-white disabled:opacity-30 disabled:cursor-not-allowed border-0"
                        title="Undo (Ctrl+Z)"
                      >
                        <Undo2 className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={handleRedo}
                        disabled={!canRedo}
                        size="sm"
                        className="bg-transparent hover:bg-slate-700/70 text-white disabled:opacity-30 disabled:cursor-not-allowed border-0"
                        title="Redo (Ctrl+Y)"
                      >
                        <Redo2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Save/Load Buttons */}
                    <div className="flex gap-1 bg-slate-800/60 backdrop-blur-md border border-slate-600/50 rounded-lg p-1">
                      <Button
                        onClick={handleSaveProject}
                        size="sm"
                        className="bg-transparent hover:bg-slate-700/70 text-white border-0"
                        title="Save Project"
                      >
                        <Save className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => loadProjectInputRef.current?.click()}
                        size="sm"
                        className="bg-transparent hover:bg-slate-700/70 text-white border-0"
                        title="Load Project"
                      >
                        <FolderOpen className="w-4 h-4" />
                      </Button>
                      {recentProjects.length > 0 && (
                        <div className="relative">
                          <Button
                            onClick={() => setShowRecentProjects(!showRecentProjects)}
                            size="sm"
                            className="bg-transparent hover:bg-slate-700/70 text-white border-0"
                            title="Recent Projects"
                          >
                            <Clock className="w-4 h-4" />
                          </Button>
                          
                          {showRecentProjects && (
                            <div className="absolute top-full left-0 mt-2 bg-slate-800/95 backdrop-blur-md border border-slate-600/50 rounded-lg shadow-2xl p-2 space-y-1 min-w-[280px] max-h-[300px] overflow-y-auto">
                              <div className="px-2 py-1 text-xs font-semibold text-slate-400 uppercase">Recent Projects</div>
                              {recentProjects.map((project) => (
                                <div
                                  key={project.id}
                                  className="flex items-center justify-between p-2 rounded hover:bg-slate-700 transition-all group"
                                >
                                  <button
                                    onClick={() => handleLoadRecent(project)}
                                    className="flex-1 text-left"
                                  >
                                    <div className="text-sm text-white font-medium truncate">{project.name}</div>
                                    <div className="text-xs text-slate-400">
                                      {new Date(project.timestamp).toLocaleString()}
                                    </div>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteRecent(project.id);
                                    }}
                                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded transition-all"
                                  >
                                    <Trash2 className="w-3 h-3 text-red-400" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Copy/Paste Buttons */}
                    {copiedLight && (
                      <Button
                        onClick={handlePasteLight}
                        size="sm"
                        className="bg-slate-800/60 backdrop-blur-md hover:bg-slate-700/70 text-white border border-slate-600/50 shadow-lg"
                        title="Paste copied light"
                      >
                        <Clipboard className="w-4 h-4 mr-2" />
                        Paste Light
                      </Button>
                    )}

               </div>
              
              {/* Auto-save indicator */}
              {lastAutoSave && (
                <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-800/60 backdrop-blur-md border border-slate-600/50 rounded-lg text-xs">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                  <span className="text-slate-400">
                    Auto-saved {lastAutoSave.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              )}

              {/* Account pill — shows login button when signed-out, or user info when signed-in */}
              {!signedIn ? (
                <Button
                  onClick={() => navigate('/login')}
                  size="sm"
                  data-testid="topbar-login-btn"
                  className="bg-slate-800/60 backdrop-blur-md hover:bg-slate-700/70 text-white border border-slate-600/50 shadow-lg"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign in
                </Button>
              ) : (
                <div className="flex items-center gap-1 bg-slate-800/60 backdrop-blur-md border border-slate-600/50 rounded-lg p-1 shadow-lg" data-testid="topbar-account-pill">
                  <div className="flex items-center gap-2 px-2 py-1 text-xs">
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="w-5 h-5 rounded-full" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-[10px] font-bold text-white">
                        {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="text-slate-300 max-w-[120px] truncate hidden sm:inline">
                      {user?.name || user?.email}
                    </span>
                    {isPro ? (
                      <span className="text-[10px] uppercase tracking-wider text-violet-300 bg-violet-500/20 border border-violet-500/30 px-1.5 py-0.5 rounded">
                        <Crown className="w-3 h-3 inline mr-0.5" />Pro
                      </span>
                    ) : (
                      <button
                        onClick={() => { setUpgradeReason('Pro features'); setShowUpgradeModal(true); }}
                        data-testid="topbar-free-upgrade-pill-editor"
                        className="text-[10px] uppercase tracking-wider text-amber-300 bg-amber-500/20 border border-amber-500/30 hover:bg-amber-500/30 px-1.5 py-0.5 rounded transition"
                        title="Upgrade to Pro"
                      >
                        Free · Upgrade
                      </button>
                    )}
                  </div>
                  {isAdmin && (
                    <Button
                      onClick={() => navigate('/admin')}
                      size="sm"
                      data-testid="topbar-admin-btn"
                      className="bg-transparent hover:bg-slate-700/70 text-amber-400 border-0 h-7 px-2"
                      title="Owner dashboard"
                    >
                      <Shield className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button
                    onClick={async () => { await logout(); navigate('/login'); }}
                    size="sm"
                    data-testid="topbar-logout-btn"
                    className="bg-transparent hover:bg-slate-700/70 text-slate-400 hover:text-white border-0 h-7 px-2"
                    title="Sign out"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Button
                    onClick={() => setShowAddMenu(!showAddMenu)}
                    className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white shadow-lg"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Light
                  </Button>
                  
                  {showAddMenu && (
                    <div className="absolute top-full right-0 mt-2 bg-slate-800/95 backdrop-blur-sm border border-slate-600 rounded-lg shadow-2xl p-2 space-y-1 min-w-[200px]">
                      <button
                        onClick={() => handleAddLight('narrow')}
                        className="w-full px-4 py-3 text-left rounded-lg hover:bg-slate-700 transition-all text-white border border-slate-600"
                      >
                        <div className="font-semibold">⚡ Narrow Beam</div>
                        <div className="text-xs text-slate-400">25° focused spotlight</div>
                      </button>
                      <button
                        onClick={() => handleAddLight('medium')}
                        className="w-full px-4 py-3 text-left rounded-lg hover:bg-slate-700 transition-all text-white border border-slate-600"
                      >
                        <div className="font-semibold">💡 Medium Beam</div>
                        <div className="text-xs text-slate-400">45° balanced spread</div>
                      </button>
                      <button
                        onClick={() => handleAddLight('wide')}
                        className="w-full px-4 py-3 text-left rounded-lg hover:bg-slate-700 transition-all text-white border border-slate-600"
                      >
                        <div className="font-semibold">🌟 Wide Beam</div>
                        <div className="text-xs text-slate-400">70° broad illumination</div>
                      </button>
                      <button
                        onClick={() => handleAddLight('grazer')}
                        className="w-full px-4 py-3 text-left rounded-lg hover:bg-slate-700 transition-all text-white border border-slate-600 border-amber-500/50"
                      >
                        <div className="font-semibold">🔆 Grazer Linear Light</div>
                        <div className="text-xs text-slate-400">Wall washer for facades</div>
                      </button>
                      <div className="border-t border-slate-600 my-2 pt-2">
                        <div className="text-xs text-slate-500 px-2 mb-1">NEON LIGHTS</div>
                      </div>
                      <button
                        onClick={() => handleAddLight('neon')}
                        className="w-full px-4 py-3 text-left rounded-lg hover:bg-slate-700 transition-all text-white border border-slate-600 border-pink-500/50"
                      >
                        <div className="font-semibold">✨ Neon Freeform</div>
                        <div className="text-xs text-slate-400">Draw freeform neon path</div>
                      </button>
                      <button
                        onClick={() => handleAddLight('smartshape')}
                        className="w-full px-4 py-3 text-left rounded-lg hover:bg-slate-700 transition-all text-white border border-slate-600 border-cyan-500/50"
                      >
                        <div className="font-semibold">🔷 Neon Smart Shape</div>
                        <div className="text-xs text-slate-400">Auto-correct to line, circle, rectangle, polygon</div>
                      </button>
                    </div>
                  )}
                  </div>
                  
                  <div className="flex gap-2 flex-wrap justify-end max-w-full">
                    <Button
                      onClick={() => setPreviewMode(true)}
                      className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white shadow-lg"
                      title="Preview mode"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </Button>

                    <Button
                      onClick={() => (isAnimating ? stopAnimation() : startAnimation())}
                      disabled={lights.length === 0}
                      data-testid="animate-timeline-btn"
                      className={`${
                        isAnimating
                          ? 'bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700'
                          : 'bg-gradient-to-r from-cyan-500 to-sky-600 hover:from-cyan-600 hover:to-sky-700'
                      } text-white shadow-lg disabled:opacity-40 disabled:cursor-not-allowed`}
                      title={
                        isAnimating
                          ? 'Stop timeline animation'
                          : 'Play lights ON one-by-one — great for screen-recording demos'
                      }
                    >
                      {isAnimating ? (
                        <>
                          <Square className="w-4 h-4 mr-2" />
                          Stop ({animationVisibleCount}/{lights.length})
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Animate
                        </>
                      )}
                    </Button>

                    {/* Speed + loop controls — compact strip next to Animate */}
                    {!isAnimating && lights.length > 0 && (
                      <div
                        className="flex items-center gap-1 bg-slate-800/60 backdrop-blur-md border border-slate-600/50 rounded-md px-1.5 py-1 shadow-lg"
                        data-testid="animate-timeline-controls"
                      >
                        <span className="text-[10px] text-slate-400 uppercase tracking-wider px-1">Speed</span>
                        {[
                          { ms: 200, label: 'Fast' },
                          { ms: 400, label: 'Norm' },
                          { ms: 800, label: 'Slow' },
                        ].map(({ ms, label }) => (
                          <button
                            key={ms}
                            type="button"
                            onClick={() => setAnimationSpeedPersisted(ms)}
                            data-testid={`animate-speed-${ms}`}
                            className={`text-[11px] px-1.5 py-0.5 rounded ${
                              animationSpeed === ms
                                ? 'bg-cyan-500 text-white'
                                : 'text-slate-300 hover:bg-slate-700'
                            }`}
                            title={`${label} — ${ms}ms per light`}
                          >
                            {label}
                          </button>
                        ))}
                        <span className="text-slate-700 mx-0.5">|</span>
                        <button
                          type="button"
                          onClick={toggleAnimationLoop}
                          data-testid="animate-loop-toggle"
                          className={`text-[11px] px-1.5 py-0.5 rounded ${
                            animationLoop
                              ? 'bg-amber-500 text-white'
                              : 'text-slate-300 hover:bg-slate-700'
                          }`}
                          title={animationLoop ? 'Loop ON — keeps replaying' : 'Loop OFF — plays once'}
                        >
                          {animationLoop ? 'Loop ●' : 'Loop ○'}
                        </button>
                      </div>
                    )}
                    
                    <Button
                      onClick={() => setPresentationMode(true)}
                      className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg"
                      title="Presentation mode with before/after comparison"
                    >
                      <Presentation className="w-4 h-4 mr-2" />
                      Present
                    </Button>
                    
                    <Button
                      onClick={handleExport}
                      className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                    
                    <Button
                      onClick={handleBatchExport}
                      className="bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white shadow-lg"
                      title="Export Daylight, Evening & Night versions"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Batch Export
                    </Button>
                  </div>
                </div>
              </div>
            </>
            )}

            {/* Preview Mode Overlay */}
            {previewMode && (
              <div className="absolute inset-0 flex items-center justify-center z-50">
                <div className="absolute top-4 right-4 flex gap-2">
                  <Button
                    onClick={handleExport}
                    className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-xl"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                  <Button
                    onClick={() => setPreviewMode(false)}
                    className="bg-slate-800/80 backdrop-blur-md hover:bg-slate-700/80 text-white shadow-xl"
                  >
                    <EyeOff className="w-4 h-4 mr-2" />
                    Exit Preview
                  </Button>
                </div>
              </div>
            )}
            
            {!previewMode && (
              <>
                {/* Ambient Darkness Slider - Bottom Right */}
                <div className="absolute bottom-4 right-4 w-80 z-10">
                  <div className="bg-slate-800/80 backdrop-blur-md border border-slate-600/50 rounded-xl p-3 shadow-2xl">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <CloudSun className="w-4 h-4 text-amber-400" />
                    <label className="text-xs font-semibold text-white">
                      Ambient Light
                    </label>
                  </div>
                  <span className="text-xs font-bold text-cyan-400">{getAmbientLabel()}</span>
                </div>
                <Slider
                  value={[ambientDarkness]}
                  onValueChange={(value) => setAmbientDarkness(value[0])}
                  min={0}
                  max={100}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-[10px] text-slate-400 mt-1.5">
                  <span>🌅 Daylight</span>
                  <span>🌆 Evening</span>
                  <span>🌃 Night</span>
                  </div>
                </div>
              </div>
            
              {/* Instructions Overlay */}
              {lights.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="bg-slate-800/70 backdrop-blur-md border border-slate-600/50 rounded-xl p-6 text-center shadow-2xl mb-32">
                    <h3 className="text-xl font-bold text-white mb-2">Ready to Design</h3>
                    <p className="text-slate-300">Click "Add Light" to place your first light source</p>
                  </div>
                </div>
              )}
            </>
            )}
          </div>

          {/* Resizable Side Panel - All Controls - Hidden in preview mode */}
          {!previewMode && (
            <div 
              className="relative flex flex-col transition-all duration-200"
              style={{ width: `${sidebarWidth}%`, maxHeight: 'calc(100vh - 2rem)' }}
            >
              {/* Resize Handle */}
              <div
                className="absolute left-0 top-0 bottom-0 w-6 cursor-col-resize group hover:bg-cyan-500/10 transition-colors z-20"
                onMouseDown={handleResizeStart}
                onTouchStart={handleResizeStart}
                style={{ touchAction: 'none' }}
              >
                <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-slate-700/80 group-hover:bg-cyan-500/50 rounded-full p-2 shadow-lg transition-all">
                  <GripVertical className="w-4 h-4 text-slate-400 group-hover:text-cyan-300" />
                </div>
                {/* Visual indicator line */}
                <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-slate-600/50 group-hover:bg-cyan-500/50 transition-colors" />
              </div>
              
              {/* Sidebar Content - Scrollable */}
              <div className="pl-8 pr-2 flex-1 overflow-y-auto space-y-3 pb-4">
            {/* Lights List - Collapsible */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-xl overflow-hidden">
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggleSection('lights')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection('lights'); } }}
                className="w-full flex items-center justify-between p-3 hover:bg-slate-700/30 transition-colors cursor-pointer"
                data-testid="lights-section-header"
              >
                <div className="flex items-center gap-2">
                  {collapsedSections.lights ? (
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  )}
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                    Lights ({lights.length})
                  </h3>
                </div>
                {lights.length >= 2 && !collapsedSections.lights && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleLightsViewMode(); }}
                      className="bg-slate-700 hover:bg-slate-600 text-white rounded px-1.5 h-6 flex items-center gap-1 text-[11px]"
                      title={`Switch to ${lightsViewMode === 'list' ? 'grid' : 'list'} view`}
                      data-testid="lights-view-toggle"
                    >
                      {lightsViewMode === 'list' ? (
                        <LayoutGrid className="w-3 h-3" />
                      ) : (
                        <List className="w-3 h-3" />
                      )}
                    </button>
                    <Button
                      onClick={(e) => { e.stopPropagation(); toggleSelectionMode(); }}
                      size="sm"
                      className={`${selectionMode ? 'bg-cyan-500 hover:bg-cyan-600' : 'bg-slate-700 hover:bg-slate-600'} text-white text-xs px-2 py-1 h-6`}
                      data-testid="lights-selection-toggle"
                    >
                      <Users className="w-3 h-3 mr-1" />
                      {selectionMode ? 'Cancel' : 'Select'}
                    </Button>
                  </div>
                )}
              </div>
              
              {!collapsedSections.lights && (
                <div className="px-3 pb-3">
              {selectionMode && selectedLights.length >= 1 && (
                <div className="flex gap-1.5 mb-3">
                  <Button
                    onClick={handleCreateGroup}
                    size="sm"
                    disabled={selectedLights.length < 2}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="lights-create-group-btn"
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Group ({selectedLights.length})
                  </Button>
                  <Button
                    onClick={handleDeleteSelectedLights}
                    size="sm"
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    data-testid="lights-delete-selected-btn"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete ({selectedLights.length})
                  </Button>
                </div>
              )}
              
              {lights.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-4">No lights added</p>
              ) : (
                <div
                  className={
                    lightsViewMode === 'grid'
                      ? 'grid grid-cols-3 gap-1.5'
                      : 'space-y-1'
                  }
                  data-testid={`lights-${lightsViewMode}-view`}
                >
                  {lights.map((light, index) => {
                    // Check if light is in any group
                    const lightGroup = groups.find(g => g.lightIds.includes(light.id));
                    const isSelected = selectedLights.includes(light.id);
                    
                    // Get icon based on light type
                    const getTypeIcon = () => {
                      switch(light.beamType) {
                        case 'narrow': return '⚡';
                        case 'medium': return '💡';
                        case 'wide': return '🌟';
                        case 'grazer': return '🔆';
                        case 'neon': return light.shapeType === 'circle' ? '⭕' : light.shapeType === 'rectangle' ? '⬜' : light.shapeType === 'triangle' ? '🔺' : light.shapeType === 'straight line' ? '➖' : '✨';
                        default: return '💡';
                      }
                    };
                    
                    // Get short type label
                    const getTypeLabel = () => {
                      if (light.beamType === 'neon') return light.shapeType || 'neon';
                      if (light.beamType === 'grazer') return 'grazer';
                      return light.beamType;
                    };

                    if (lightsViewMode === 'grid') {
                      // Compact square tile layout
                      const isDragOver = dragOverLightIdx === index && draggedLightIdx !== null && draggedLightIdx !== index;
                      const isBeingDragged = draggedLightIdx === index;
                      return (
                        <div
                          key={light.id}
                          draggable={!selectionMode}
                          onDragStart={(e) => {
                            if (selectionMode) return;
                            setDraggedLightIdx(index);
                            try { e.dataTransfer.effectAllowed = 'move'; } catch (_) {}
                          }}
                          onDragOver={(e) => {
                            if (selectionMode || draggedLightIdx === null) return;
                            e.preventDefault();
                            if (dragOverLightIdx !== index) setDragOverLightIdx(index);
                          }}
                          onDragLeave={() => {
                            if (dragOverLightIdx === index) setDragOverLightIdx(null);
                          }}
                          onDrop={(e) => {
                            if (selectionMode) return;
                            e.preventDefault();
                            if (draggedLightIdx !== null && draggedLightIdx !== index) {
                              reorderLights(draggedLightIdx, index);
                            }
                            setDraggedLightIdx(null);
                            setDragOverLightIdx(null);
                          }}
                          onDragEnd={() => {
                            setDraggedLightIdx(null);
                            setDragOverLightIdx(null);
                          }}
                          onClick={() => selectionMode ? toggleLightSelection(light.id) : setSelectedLightId(light.id)}
                          data-testid={`light-tile-${light.id}`}
                          className={`relative aspect-square rounded-md border transition-all flex flex-col items-center justify-center gap-0.5 p-1 group ${
                            selectionMode ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'
                          } ${
                            isBeingDragged
                              ? 'opacity-40 scale-95 border-cyan-400'
                              : isDragOver
                              ? 'border-amber-400 bg-amber-500/20 shadow-lg shadow-amber-500/40'
                              : selectionMode && isSelected
                              ? 'border-green-400 bg-green-500/20'
                              : selectedLightId === light.id
                              ? 'border-cyan-400 bg-cyan-500/20 shadow-md shadow-cyan-500/20'
                              : 'border-slate-600/50 bg-slate-700/20 hover:border-slate-500 hover:bg-slate-700/40'
                          }`}
                          title={
                            selectionMode
                              ? `${index + 1}. ${getTypeLabel()}${lightGroup ? ` · ${lightGroup.name}` : ''}`
                              : `Drag to reorder · ${index + 1}. ${getTypeLabel()}${lightGroup ? ` · ${lightGroup.name}` : ''}`
                          }
                        >
                          {selectionMode && (
                            <div
                              className={`absolute top-1 left-1 w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center ${
                                isSelected ? 'bg-green-500 border-green-500' : 'border-slate-500 bg-slate-900/60'
                              }`}
                            >
                              {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                            </div>
                          )}
                          <div
                            className="w-4 h-4 rounded-full border-2"
                            style={{
                              backgroundColor: light.customColor || '#FFF5DC',
                              borderColor: lightGroup ? '#F59E0B' : 'rgba(255,255,255,0.1)',
                              boxShadow: `0 0 8px ${light.customColor || '#FFF5DC'}`,
                            }}
                          />
                          <span className="text-base leading-none">{getTypeIcon()}</span>
                          <span className="text-[10px] text-slate-400 leading-none">#{index + 1}</span>
                          {!selectionMode && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteLight(light.id);
                              }}
                              className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 p-0.5 hover:bg-red-500/20 rounded transition-all"
                              data-testid={`light-tile-delete-${light.id}`}
                            >
                              <Trash2 className="w-3 h-3 text-red-400" />
                            </button>
                          )}
                        </div>
                      );
                    }

                    return (
                      <div
                        key={light.id}
                        onClick={() => selectionMode ? toggleLightSelection(light.id) : setSelectedLightId(light.id)}
                        className={`w-full px-2 py-1.5 rounded-md border transition-all text-left flex items-center gap-2 group cursor-pointer ${
                          selectionMode && isSelected
                            ? 'border-green-400 bg-green-500/20'
                            : selectedLightId === light.id
                            ? 'border-cyan-400 bg-cyan-500/20'
                            : 'border-slate-600/50 bg-slate-700/20 hover:border-slate-500 hover:bg-slate-700/40'
                        }`}
                      >
                        {selectionMode && (
                          <div className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center ${
                            isSelected ? 'bg-green-500 border-green-500' : 'border-slate-500'
                          }`}>
                            {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                          </div>
                        )}
                        
                        {/* Color indicator */}
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0 border"
                          style={{ 
                            backgroundColor: light.customColor || '#FFF5DC',
                            borderColor: lightGroup ? '#F59E0B' : 'transparent'
                          }}
                        />
                        
                        {/* Type icon */}
                        <span className="text-xs flex-shrink-0">{getTypeIcon()}</span>
                        
                        {/* Light info - compact */}
                        <div className="flex-1 min-w-0 flex items-center gap-1.5">
                          <span className={`text-xs font-medium truncate ${
                            selectedLightId === light.id ? 'text-white' : 'text-slate-300'
                          }`}>
                            {index + 1}. {getTypeLabel()}
                          </span>
                          {lightGroup && (
                            <span className="text-[10px] text-amber-400 bg-amber-500/20 px-1 rounded flex-shrink-0">
                              {lightGroup.name}
                            </span>
                          )}
                        </div>
                        
                        {/* Temperature/Color badge */}
                        <span className="text-[10px] text-slate-500 flex-shrink-0">
                          {light.customColor ? 'RGB' : `${light.colorTemp}K`}
                        </span>
                        
                        {/* Delete button */}
                        {!selectionMode && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteLight(light.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-red-500/20 rounded transition-all flex-shrink-0"
                          >
                            <Trash2 className="w-3 h-3 text-red-400" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
                </div>
              )}
            </div>

            {/* Groups List */}
            {groups.length > 0 && (
              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 shadow-xl">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                    Groups ({groups.length})
                  </h3>
                </div>
                
                <div className="space-y-2">
                  {groups.map((group) => {
                    const isHidden = hiddenGroups.includes(group.id);
                    return (
                      <div
                        key={group.id}
                        className={`p-3 rounded-lg border transition-all ${
                          selectedGroupId === group.id
                            ? 'border-amber-400 bg-gradient-to-br from-amber-500/20 to-orange-500/20 shadow-lg'
                            : isHidden
                            ? 'border-slate-700 bg-slate-800/50 opacity-60'
                            : 'border-slate-600 bg-slate-700/30'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => handleSelectGroup(group.id)}
                            className="flex-1 text-left"
                          >
                            <div className="text-sm font-semibold text-white flex items-center gap-2">
                              <Users className="w-3 h-3 text-amber-400" />
                              {group.name}
                              {isHidden && <span className="text-xs text-slate-500">(hidden)</span>}
                            </div>
                            <div className="text-xs text-slate-400">
                              {group.lightIds.length} lights
                            </div>
                          </button>
                          <div className="flex items-center gap-1">
                            {/* Hide/Show Toggle */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleGroupVisibility(group.id);
                              }}
                              className={`p-1.5 rounded transition-all ${
                                isHidden 
                                  ? 'bg-slate-600 hover:bg-slate-500' 
                                  : 'hover:bg-cyan-500/20'
                              }`}
                              title={isHidden ? 'Show group' : 'Hide group'}
                            >
                              {isHidden ? (
                                <EyeOff className="w-3.5 h-3.5 text-slate-400" />
                              ) : (
                                <Eye className="w-3.5 h-3.5 text-cyan-400" />
                              )}
                            </button>
                            {/* Delete Button */}
                            <button
                              onClick={() => handleDeleteGroup(group.id)}
                              className="p-1.5 hover:bg-red-500/20 rounded transition-all"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Selected Light Controls */}
            {selectedLight && (
              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 shadow-xl space-y-4">
                <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">
                  {selectedLight.beamType === 'neon' 
                    ? `Neon ${selectedLight.shapeType ? `(${selectedLight.shapeType})` : ''} Controls`
                    : selectedLight.beamType === 'grazer' ? 'Grazer Light Controls' 
                    : 'Light Controls'}
                </h3>
                
                {/* Color Temperature Slider */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <Sun className="w-4 h-4 text-amber-400" />
                      Color Temperature
                    </label>
                    <span className="text-sm font-bold text-white">
                      {selectedLight.colorTemp || 2700}K
                    </span>
                  </div>
                  <Slider
                    value={[selectedLight.colorTemp || 2700]}
                    onValueChange={(value) => handleColorTempChange(value[0])}
                    min={2200}
                    max={6500}
                    step={100}
                    className="w-full"
                    disabled={!!selectedLight.customColor}
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>2200K Warm</span>
                    <span className="text-amber-400">3000K</span>
                    <span>6500K Cool</span>
                  </div>
                  {selectedLight.customColor && (
                    <p className="text-xs text-orange-400 mt-2">
                      Using custom color - Use "Reset to preset" below to enable slider
                    </p>
                  )}
                  {selectedGroupId && !selectedLight.customColor && (
                    <p className="text-xs text-cyan-400 mt-2">
                      🔗 Group mode: Changes apply to all {groups.find(g => g.id === selectedGroupId)?.lightIds.length || 0} lights
                    </p>
                  )}
                </div>
                
                {/* Custom RGB Color */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <Palette className="w-4 h-4 text-pink-400" />
                      Custom Color
                    </label>
                    {selectedLight.customColor && (
                      <button
                        onClick={() => handleColorTempChange(2700)}
                        className="text-xs text-slate-400 hover:text-white transition-colors"
                      >
                        Reset to preset
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={selectedLight.customColor || '#FFF5DC'}
                      onChange={(e) => handleCustomColorChange(e.target.value)}
                      className="w-full h-10 rounded-lg cursor-pointer border-2 border-slate-600 bg-slate-700"
                      style={{ colorScheme: 'dark' }}
                    />
                    <div className="text-xs text-slate-400 font-mono">
                      {selectedLight.customColor || 'Default'}
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Choose any RGB color for custom effects</p>
                </div>

                {/* Neon-specific controls */}
                {selectedLight.beamType === 'neon' && (
                  <>
                    {/* Neon Brightness */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                          <Sun className="w-4 h-4 text-pink-400" />
                          Brightness
                        </label>
                        <span className="text-sm font-bold text-white">{selectedLight.brightness}%</span>
                      </div>
                      <Slider
                        value={[selectedLight.brightness || 50]}
                        onValueChange={(value) => updateSelectedLight({ brightness: value[0] })}
                        min={10}
                        max={100}
                        step={5}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>Dim</span>
                        <span>Bright</span>
                      </div>
                    </div>

                    {/* Neon Thickness */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                          <Ruler className="w-4 h-4 text-cyan-400" />
                          Line Thickness
                        </label>
                        <span className="text-sm font-bold text-white">{selectedLight.thickness}px</span>
                      </div>
                      <Slider
                        value={[selectedLight.thickness || 3]}
                        onValueChange={(value) => updateSelectedLight({ thickness: value[0] })}
                        min={1}
                        max={20}
                        step={0.5}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>Thin</span>
                        <span>Thick</span>
                      </div>
                    </div>

                    {/* Neon Glow Radius */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                          <Lightbulb className="w-4 h-4 text-purple-400" />
                          Glow Radius
                        </label>
                        <span className="text-sm font-bold text-white">{selectedLight.glowRadius}px</span>
                      </div>
                      <Slider
                        value={[selectedLight.glowRadius || 20]}
                        onValueChange={(value) => updateSelectedLight({ glowRadius: value[0] })}
                        min={5}
                        max={50}
                        step={1}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>Subtle</span>
                        <span>Intense</span>
                      </div>
                    </div>

                    <p className="text-xs text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded p-2">
                      💡 Draw freeform neon light with magnifier lens for precision
                    </p>
                  </>
                )}

                {/* Grazer-specific controls */}
                {selectedLight.beamType === 'grazer' && (
                  <>
                    {/* Grazer Length */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                          <Ruler className="w-4 h-4 text-amber-400" />
                          Fixture Length
                        </label>
                        <span className="text-sm font-bold text-white">{selectedLight.length?.toFixed(0) || 10}%</span>
                      </div>
                      <Slider
                        value={[selectedLight.length || 10]}
                        onValueChange={(value) => {
                          const newLength = value[0];
                          const midX = (selectedLight.startPoint.x + selectedLight.endPoint.x) / 2;
                          const midY = (selectedLight.startPoint.y + selectedLight.endPoint.y) / 2;
                          const angle = Math.atan2(
                            selectedLight.endPoint.y - selectedLight.startPoint.y,
                            selectedLight.endPoint.x - selectedLight.startPoint.x
                          );
                          const halfLen = newLength / 2;
                          updateSelectedLight({
                            length: newLength,
                            startPoint: {
                              x: midX - Math.cos(angle) * halfLen,
                              y: midY - Math.sin(angle) * halfLen
                            },
                            endPoint: {
                              x: midX + Math.cos(angle) * halfLen,
                              y: midY + Math.sin(angle) * halfLen
                            }
                          });
                        }}
                        min={5}
                        max={80}
                        step={1}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>Short</span>
                        <span>Long</span>
                      </div>
                    </div>

                    {/* Throw Distance */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                          <Sun className="w-4 h-4 text-yellow-400" />
                          Throw Distance
                        </label>
                        <input
                          type="number"
                          value={selectedLight.throwDistance?.toFixed(2) || 1.5}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val > 0) {
                              updateSelectedLight({ throwDistance: val });
                            }
                          }}
                          className="w-16 px-2 py-1 text-sm font-bold text-white bg-slate-700 border border-slate-600 rounded text-right"
                          step="0.01"
                          min="0.01"
                        />
                      </div>
                      <Slider
                        value={[Math.max(0.05, Math.min(3, selectedLight.throwDistance || 1.5))]}
                        onValueChange={(value) => updateSelectedLight({ throwDistance: value[0] })}
                        min={0.05}
                        max={3}
                        step={0.05}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>0.05x Near</span>
                        <span>3x Far</span>
                      </div>
                    </div>

                    {/* Throw Direction */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                          <RotateCw className="w-4 h-4 text-cyan-400" />
                          Light Direction
                        </label>
                        <span className="text-sm font-bold text-white">{selectedLight.throwDirection || 270}°</span>
                      </div>
                      <Slider
                        value={[selectedLight.throwDirection || 270]}
                        onValueChange={(value) => updateSelectedLight({ throwDirection: value[0] })}
                        min={0}
                        max={359}
                        step={1}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>0° Right</span>
                        <span>180° Left</span>
                      </div>
                    </div>

                    {/* Grazer Intensity */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                          <Lightbulb className="w-4 h-4 text-amber-400" />
                          Intensity
                        </label>
                        <span className="text-sm font-bold text-white">{selectedLight.intensity}%</span>
                      </div>
                      <Slider
                        value={[selectedLight.intensity || 50]}
                        onValueChange={(value) => updateSelectedLight({ intensity: value[0] })}
                        min={0}
                        max={100}
                        step={1}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>Dim</span>
                        <span>Bright</span>
                      </div>
                    </div>

                    {/* Grazer Source Size */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                          <Maximize2 className="w-4 h-4 text-purple-400" />
                          Light Source Size
                        </label>
                        <input
                          type="number"
                          value={(selectedLight.sourceSize || 1).toFixed(2)}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val > 0) {
                              updateSelectedLight({ sourceSize: val });
                            }
                          }}
                          className="w-16 px-2 py-1 text-sm font-bold text-white bg-slate-700 border border-slate-600 rounded text-right"
                          step="0.1"
                          min="0.1"
                        />
                      </div>
                      <Slider
                        value={[Math.max(0.5, Math.min(3, selectedLight.sourceSize || 1))]}
                        onValueChange={(value) => updateSelectedLight({ sourceSize: value[0] })}
                        min={0.5}
                        max={3}
                        step={0.1}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>0.5x Small</span>
                        <span>3x Large</span>
                      </div>
                    </div>

                    {/* Grazer Hide/Show Source Toggle */}
                    <div className="flex items-center justify-between py-2">
                      <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                        {selectedLight.sourceHidden ? (
                          <EyeOff className="w-4 h-4 text-slate-400" />
                        ) : (
                          <Eye className="w-4 h-4 text-cyan-400" />
                        )}
                        Light Source Visibility
                      </label>
                      <Button
                        size="sm"
                        variant={selectedLight.sourceHidden ? "outline" : "default"}
                        onClick={() => updateSelectedLight({ sourceHidden: !selectedLight.sourceHidden })}
                        className={`gap-1 ${selectedLight.sourceHidden ? 'bg-slate-700 border-slate-600 text-slate-300' : 'bg-cyan-600 hover:bg-cyan-700 text-white'}`}
                      >
                        {selectedLight.sourceHidden ? (
                          <>
                            <EyeOff className="w-3 h-3" />
                            Hidden
                          </>
                        ) : (
                          <>
                            <Eye className="w-3 h-3" />
                            Visible
                          </>
                        )}
                      </Button>
                    </div>

                    <p className="text-xs text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded p-2">
                      🔆 Drag end handles to resize • Drag line to move • Rotate to align with architecture
                    </p>

                    {/* Realistic Wall Throw Toggle (grazer) */}
                    <div className="flex items-center justify-between py-2" data-testid="grazer-surface-throw-row">
                      <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                        <Sparkles className={`w-4 h-4 ${selectedLight.surfaceThrow ? 'text-amber-400' : 'text-slate-400'}`} />
                        Realistic Wall Throw
                      </label>
                      <Button
                        size="sm"
                        data-testid="grazer-surface-throw-toggle"
                        variant={selectedLight.surfaceThrow ? "default" : "outline"}
                        onClick={() => updateSelectedLight({ surfaceThrow: !selectedLight.surfaceThrow })}
                        className={`gap-1 ${selectedLight.surfaceThrow ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-slate-700 border-slate-600 text-slate-300'}`}
                      >
                        <Sparkles className="w-3 h-3" />
                        {selectedLight.surfaceThrow ? 'On' : 'Off'}
                      </Button>
                    </div>
                  </>
                )}

                {/* Beam-specific controls (not for neon or grazer) */}
                {selectedLight.beamType !== 'neon' && selectedLight.beamType !== 'grazer' && (
                  <>
                    {/* Light Source Size */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                          <Maximize2 className="w-4 h-4 text-purple-400" />
                          Light Source Size
                        </label>
                        <input
                          type="number"
                          value={selectedLight.sourceSize.toFixed(2)}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val > 0) {
                              updateSelectedLight({ sourceSize: val });
                            }
                          }}
                          className="w-16 px-2 py-1 text-sm font-bold text-white bg-slate-700 border border-slate-600 rounded text-right"
                          step="0.01"
                          min="0.01"
                        />
                      </div>
                      <Slider
                        value={[Math.max(0.5, Math.min(3, selectedLight.sourceSize))]}
                        onValueChange={(value) => updateSelectedLight({ sourceSize: value[0] })}
                        min={0.5}
                        max={3}
                        step={0.1}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>0.5x Small</span>
                        <span>3x Large</span>
                      </div>
                    </div>
                  </>
                )}

                {/* Beam Length */}
                {selectedLight.beamType !== 'neon' && selectedLight.beamType !== 'grazer' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                        <Ruler className="w-4 h-4 text-green-400" />
                        Beam Length
                      </label>
                      <input
                        type="number"
                        value={selectedLight.beamLength.toFixed(2)}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val) && val > 0) {
                            updateSelectedLight({ beamLength: val });
                          }
                        }}
                        className="w-16 px-2 py-1 text-sm font-bold text-white bg-slate-700 border border-slate-600 rounded text-right"
                        step="0.01"
                        min="0.01"
                      />
                    </div>
                    <Slider
                      value={[Math.max(0.05, Math.min(2, selectedLight.beamLength))]}
                      onValueChange={(value) => updateSelectedLight({ beamLength: value[0] })}
                      min={0.05}
                      max={2}
                      step={0.05}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>0.05x Short</span>
                      <span>2x Long</span>
                    </div>
                  </div>
                )}

                {/* Beam Spread */}
                {selectedLight.beamType !== 'neon' && selectedLight.beamType !== 'grazer' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                        <Sun className="w-4 h-4 text-yellow-400" />
                        Beam Spread
                      </label>
                      <span className="text-sm font-bold text-white">{selectedLight.spread}°</span>
                    </div>
                    <Slider
                      value={[selectedLight.spread]}
                      onValueChange={(value) => updateSelectedLight({ spread: value[0] })}
                      min={1}
                      max={360}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>1° Narrow</span>
                      <span>360° Full</span>
                    </div>
                  </div>
                )}

                {/* Beam Direction — Pan/Tilt 2D pad + fine slider */}
                {selectedLight.beamType !== 'neon' && selectedLight.beamType !== 'grazer' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                        <RotateCw className="w-4 h-4 text-cyan-400" />
                        Direction (Pan / Tilt)
                      </label>
                      <span className="text-sm font-bold text-white">{selectedLight.direction}°</span>
                    </div>
                    <div className="flex items-center gap-3 mb-2">
                      <BeamPanTiltPad
                        direction={selectedLight.direction}
                        onChange={(v) => updateSelectedLight({ direction: v })}
                      />
                      <div className="flex-1">
                        <Slider
                          value={[selectedLight.direction]}
                          onValueChange={(value) => updateSelectedLight({ direction: value[0] })}
                          min={0}
                          max={359}
                          step={1}
                          className="w-full"
                          data-testid="beam-direction-slider"
                        />
                        <div className="flex justify-between text-xs text-slate-500 mt-1">
                          <span>0°</span>
                          <span>360°</span>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1 leading-tight">
                          Drag the puck to aim freely; slider for fine-tune.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Terminal Section Range + Bend — architectural edge simulation */}
                {selectedLight.beamType !== 'neon' && selectedLight.beamType !== 'grazer' && (
                  <div className="pt-2 border-t border-slate-800/60">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                        <Sunset className="w-4 h-4 text-amber-400" />
                        Terminal Section (Tip)
                      </label>
                      <span className="text-sm font-bold text-white">{selectedLight.terminalRange || 0}%</span>
                    </div>
                    <Slider
                      value={[selectedLight.terminalRange || 0]}
                      onValueChange={(value) => updateSelectedLight({ terminalRange: value[0] })}
                      min={0}
                      max={100}
                      step={1}
                      className="w-full"
                      data-testid="beam-terminal-range-slider"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>Off</span>
                      <span>Full tip</span>
                    </div>

                    <div className="flex items-center justify-between mt-3 mb-2">
                      <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                        <RotateCw className="w-4 h-4 text-amber-400" />
                        Terminal Bend
                      </label>
                      <span className="text-sm font-bold text-white">{selectedLight.terminalTilt || 0}°</span>
                    </div>
                    <Slider
                      value={[selectedLight.terminalTilt || 0]}
                      onValueChange={(value) => updateSelectedLight({ terminalTilt: value[0] })}
                      min={-90}
                      max={90}
                      step={1}
                      className="w-full"
                      disabled={!(selectedLight.terminalRange > 0)}
                      data-testid="beam-terminal-tilt-slider"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>-90°</span>
                      <span>Straight</span>
                      <span>+90°</span>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1 leading-tight">
                      Isolate the tip and bend it up to 90° to wrap onto an edge.
                    </p>
                  </div>
                )}

                {/* Light Intensity (for beam lights only, neon and grazer have their own) */}
                {selectedLight.beamType !== 'neon' && selectedLight.beamType !== 'grazer' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-amber-400" />
                        Intensity
                      </label>
                      <span className="text-sm font-bold text-white">{selectedLight.intensity}%</span>
                    </div>
                    <Slider
                      value={[selectedLight.intensity]}
                      onValueChange={(value) => updateSelectedLight({ intensity: value[0] })}
                      min={0}
                      max={100}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>Dim</span>
                      <span>Bright</span>
                    </div>
                  </div>
                )}

                {/* Beam Hide/Show Source Toggle */}
                {selectedLight.beamType !== 'neon' && selectedLight.beamType !== 'grazer' && (
                  <div className="flex items-center justify-between py-2">
                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      {selectedLight.sourceHidden ? (
                        <EyeOff className="w-4 h-4 text-slate-400" />
                      ) : (
                        <Eye className="w-4 h-4 text-cyan-400" />
                      )}
                      Light Source Visibility
                    </label>
                    <Button
                      size="sm"
                      variant={selectedLight.sourceHidden ? "outline" : "default"}
                      onClick={() => updateSelectedLight({ sourceHidden: !selectedLight.sourceHidden })}
                      className={`gap-1 ${selectedLight.sourceHidden ? 'bg-slate-700 border-slate-600 text-slate-300' : 'bg-cyan-600 hover:bg-cyan-700 text-white'}`}
                    >
                      {selectedLight.sourceHidden ? (
                        <>
                          <EyeOff className="w-3 h-3" />
                          Hidden
                        </>
                      ) : (
                        <>
                          <Eye className="w-3 h-3" />
                          Visible
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {/* Realistic Wall Throw Toggle (beam lights) */}
                {selectedLight.beamType !== 'neon' && selectedLight.beamType !== 'grazer' && (
                  <div className="flex items-center justify-between py-2" data-testid="beam-surface-throw-row">
                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <Sparkles className={`w-4 h-4 ${selectedLight.surfaceThrow ? 'text-amber-400' : 'text-slate-400'}`} />
                      Realistic Wall Throw
                    </label>
                    <Button
                      size="sm"
                      data-testid="beam-surface-throw-toggle"
                      variant={selectedLight.surfaceThrow ? "default" : "outline"}
                      onClick={() => updateSelectedLight({ surfaceThrow: !selectedLight.surfaceThrow })}
                      className={`gap-1 ${selectedLight.surfaceThrow ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-slate-700 border-slate-600 text-slate-300'}`}
                    >
                      <Sparkles className="w-3 h-3" />
                      {selectedLight.surfaceThrow ? 'On' : 'Off'}
                    </Button>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="pt-3 border-t border-slate-700 space-y-2">
                  <div className="flex gap-2">
                    <button
                      onClick={handleCopyLight}
                      className="flex-1 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-all text-sm font-medium"
                    >
                      <Copy className="w-4 h-4 inline mr-2" />
                      Copy Light
                    </button>
                    <button
                      onClick={handlePasteLight}
                      disabled={!copiedLight}
                      className="flex-1 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Clipboard className="w-4 h-4 inline mr-2" />
                      Paste Light
                    </button>
                  </div>
                  <button
                    onClick={() => handleDeleteLight(selectedLight.id)}
                    className="w-full px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-all text-sm font-medium"
                  >
                    <Trash2 className="w-4 h-4 inline mr-2" />
                    Delete This Light
                  </button>
                </div>
              </div>
            )}

            {/* Occluders Section - Architectural Masks - Collapsible */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-xl overflow-hidden" data-testid="occluders-section">
              <div className="flex items-center justify-between p-3 hover:bg-slate-700/30 transition-colors">
                <button
                  onClick={() => toggleSection('occluders')}
                  className="flex items-center gap-2 flex-1 text-left"
                >
                  {collapsedSections.occluders ? (
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  )}
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                    Occluders ({occluders.length})
                  </h3>
                </button>
                <div className="flex items-center gap-1">
                  <Button
                    data-testid="draw-occluder-btn"
                    onClick={handleToggleDrawOccluder}
                    size="sm"
                    title={isDrawingOccluder ? 'Cancel drawing (ESC)' : 'Draw occluder on canvas'}
                    className={`text-xs px-2 py-1 h-6 ${isDrawingOccluder ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-slate-700 hover:bg-slate-600 text-slate-200 border border-slate-600'}`}
                  >
                    {isDrawingOccluder ? (<><X className="w-3 h-3 mr-1" />Cancel</>) : (<><Ruler className="w-3 h-3 mr-1" />Draw</>)}
                  </Button>
                  <Button
                    data-testid="add-occluder-btn"
                    onClick={handleAddOccluder}
                    size="sm"
                    title="Add occluder at center"
                    className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-xs px-2 py-1 h-6"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </div>
              </div>

              {!collapsedSections.occluders && (
                <div className="px-3 pb-3 space-y-3">
                  {isDrawingOccluder && (
                    <div className="text-xs text-amber-300 border border-amber-500/50 bg-amber-500/15 rounded p-2 leading-relaxed" data-testid="occluder-drawing-banner">
                      ✏️ <b>Freehand mode</b>: press &amp; drag on the canvas to trace <b>any shape</b> around an overhang, column, cornice, or balcony slab. Release to close the shape. Press <kbd className="px-1 bg-slate-800 rounded">ESC</kbd> to cancel.
                    </div>
                  )}
                  <p className="text-xs text-slate-400 border border-slate-700 bg-slate-900/40 rounded p-2 leading-relaxed">
                    Draw masks over soffits, overhangs, columns, or balcony slabs to cast realistic shadow edges. Lights clip against these shapes with soft edges.
                  </p>

                  {occluders.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-2">No occluders yet. Click "Add" to place one.</p>
                  )}

                  {occluders.length > 0 && (
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {occluders.map((occ, idx) => (
                        <div
                          key={occ.id}
                          data-testid={`occluder-item-${occ.id}`}
                          onClick={() => { setSelectedOccluderId(occ.id); setSelectedLightId(null); }}
                          className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-all ${
                            selectedOccluderId === occ.id
                              ? 'bg-amber-500/20 border border-amber-400/50'
                              : 'bg-slate-700/30 border border-slate-600/30 hover:bg-slate-700/50'
                          }`}
                        >
                          <div className={`w-3 h-3 border-2 ${occ.hidden ? 'border-slate-500 border-dashed opacity-50' : 'border-amber-400 border-dashed'}`} />
                          <span className={`text-xs flex-1 ${occ.hidden ? 'text-slate-500 italic' : 'text-slate-300'}`}>
                            {occ.name || `Occluder ${idx + 1}`}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleUpdateOccluder(occ.id, { hidden: !occ.hidden }); }}
                            className="p-1 hover:bg-slate-600/50 rounded"
                            title={occ.hidden ? "Show" : "Hide"}
                          >
                            {occ.hidden ? <EyeOff className="w-3 h-3 text-slate-400" /> : <Eye className="w-3 h-3 text-cyan-400" />}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteOccluder(occ.id); }}
                            className="p-1 hover:bg-red-500/20 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3 text-red-400" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Selected Occluder Controls */}
                  {selectedOccluder && (
                    <div className="space-y-3 pt-2 border-t border-slate-700" data-testid="occluder-controls">
                      <div>
                        <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Name</label>
                        <input
                          type="text"
                          value={selectedOccluder.name || ''}
                          onChange={(e) => updateSelectedOccluder({ name: e.target.value })}
                          className="w-full mt-1 px-2 py-1 bg-slate-900/70 border border-slate-600 rounded text-sm text-white focus:outline-none focus:border-amber-500"
                        />
                      </div>

                      {selectedOccluder.type === 'polygon' ? (
                        <div className="text-xs text-cyan-300 border border-cyan-500/30 bg-cyan-500/10 rounded p-2">
                          🔷 Freehand polygon · <b>{selectedOccluder.points?.length || 0}</b> vertices. Drag the outline on canvas to move. To reshape, delete &amp; re-draw.
                        </div>
                      ) : (
                        <>
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-sm font-medium text-slate-300">Width</label>
                              <span className="text-sm font-bold text-white">{selectedOccluder.width.toFixed(1)}%</span>
                            </div>
                            <Slider
                              value={[selectedOccluder.width]}
                              onValueChange={(v) => updateSelectedOccluder({ width: v[0] })}
                              min={1} max={100} step={0.5}
                            />
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-sm font-medium text-slate-300">Height</label>
                              <span className="text-sm font-bold text-white">{selectedOccluder.height.toFixed(1)}%</span>
                            </div>
                            <Slider
                              value={[selectedOccluder.height]}
                              onValueChange={(v) => updateSelectedOccluder({ height: v[0] })}
                              min={0.5} max={100} step={0.5}
                            />
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-sm font-medium text-slate-300">Rotation</label>
                              <span className="text-sm font-bold text-white">{Math.round(selectedOccluder.rotation || 0)}°</span>
                            </div>
                            <Slider
                              value={[selectedOccluder.rotation || 0]}
                              onValueChange={(v) => updateSelectedOccluder({ rotation: v[0] })}
                              min={-180} max={180} step={1}
                            />
                          </div>
                        </>
                      )}

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-sm font-medium text-slate-300">Edge Softness</label>
                          <span className="text-sm font-bold text-white">{selectedOccluder.softness ?? 12}px</span>
                        </div>
                        <Slider
                          value={[selectedOccluder.softness ?? 12]}
                          onValueChange={(v) => updateSelectedOccluder({ softness: v[0] })}
                          min={0} max={60} step={1}
                        />
                        <p className="text-[10px] text-slate-500 mt-1">Higher = softer shadow edges</p>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-sm font-medium text-slate-300">Blocking Strength</label>
                          <span className="text-sm font-bold text-white">{Math.round((selectedOccluder.opacity ?? 1) * 100)}%</span>
                        </div>
                        <Slider
                          value={[(selectedOccluder.opacity ?? 1) * 100]}
                          onValueChange={(v) => updateSelectedOccluder({ opacity: v[0] / 100 })}
                          min={0} max={100} step={1}
                        />
                        <p className="text-[10px] text-slate-500 mt-1">100% = full block, lower = partial shadow</p>
                      </div>

                      <p className="text-[11px] text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded p-2">
                        💡 {selectedOccluder.type === 'polygon'
                          ? <>Drag the outline to move. Shortcuts: <kbd className="px-1 bg-slate-800 rounded">Ctrl+D</kbd> duplicate, <kbd className="px-1 bg-slate-800 rounded">Ctrl+C</kbd>/<kbd className="px-1 bg-slate-800 rounded">Ctrl+V</kbd> copy/paste.</>
                          : <>Drag outline or <b>corner handles</b> on canvas to move/resize. Shortcuts: <kbd className="px-1 bg-slate-800 rounded">Ctrl+D</kbd> duplicate, <kbd className="px-1 bg-slate-800 rounded">Ctrl+C</kbd>/<kbd className="px-1 bg-slate-800 rounded">Ctrl+V</kbd> copy/paste.</>
                        }
                      </p>

                      <div className="grid grid-cols-3 gap-2">
                        <button
                          data-testid="occluder-duplicate-btn"
                          onClick={() => handleDuplicateOccluder(selectedOccluder.id)}
                          className="px-2 py-2 bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 rounded-lg transition-all text-xs font-medium flex items-center justify-center gap-1"
                          title="Duplicate (Ctrl+D)"
                        >
                          <Copy className="w-3 h-3" />
                          Duplicate
                        </button>
                        <button
                          data-testid="occluder-copy-btn"
                          onClick={handleCopyOccluder}
                          className="px-2 py-2 bg-slate-600/40 hover:bg-slate-600/60 text-slate-200 rounded-lg transition-all text-xs font-medium flex items-center justify-center gap-1"
                          title="Copy (Ctrl+C)"
                        >
                          <Clipboard className="w-3 h-3" />
                          Copy
                        </button>
                        <button
                          data-testid="occluder-paste-btn"
                          onClick={handlePasteOccluder}
                          disabled={!copiedOccluder}
                          className="px-2 py-2 bg-slate-600/40 hover:bg-slate-600/60 text-slate-200 rounded-lg transition-all text-xs font-medium flex items-center justify-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Paste (Ctrl+V)"
                        >
                          <Clipboard className="w-3 h-3" />
                          Paste
                        </button>
                      </div>

                      <button
                        onClick={() => handleDeleteOccluder(selectedOccluder.id)}
                        className="w-full px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-all text-sm font-medium"
                      >
                        <Trash2 className="w-4 h-4 inline mr-2" />
                        Delete This Occluder
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Logo Settings - Multiple Logos Support - Collapsible */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-xl overflow-hidden">
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggleSection('logos')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection('logos'); } }}
                className="w-full flex items-center justify-between p-3 hover:bg-slate-700/30 transition-colors cursor-pointer"
                data-testid="logos-section-header"
              >
                <div className="flex items-center gap-2">
                  {collapsedSections.logos ? (
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  )}
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                    Logos ({logos.length})
                  </h3>
                </div>
                <Button
                  onClick={(e) => { e.stopPropagation(); logoInputRef.current?.click(); }}
                  size="sm"
                  className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white text-xs px-2 py-1 h-6"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add
                </Button>
              </div>
              
              {!collapsedSections.logos && (
                <div className="px-3 pb-3 space-y-3">
              {/* Logo List */}
              {logos.length > 0 && (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {logos.map((logo, index) => (
                    <div
                      key={logo.id}
                      onClick={() => setSelectedLogoId(logo.id)}
                      className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-all ${
                        selectedLogoId === logo.id
                          ? 'bg-cyan-500/20 border border-cyan-400/50'
                          : 'bg-slate-700/30 border border-slate-600/30 hover:bg-slate-700/50'
                      }`}
                    >
                      <img 
                        src={logo.image} 
                        alt={`Logo ${index + 1}`}
                        className="w-8 h-8 object-contain rounded"
                      />
                      <span className="text-xs text-slate-300 flex-1">Logo {index + 1}</span>
                      <span className="text-xs text-slate-500">{logo.size}%</span>
                    </div>
                  ))}
                </div>
              )}
              
              {logos.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-2">No logos added yet</p>
              )}
              
              {/* Selected Logo Controls */}
              {selectedLogo && (
                <>
                  {/* Logo Preview */}
                  <div className="relative bg-slate-700/30 rounded-lg p-3 border border-slate-600">
                    <img 
                      src={selectedLogo.image} 
                      alt="Logo preview" 
                      className="w-full h-16 object-contain"
                      style={{ opacity: selectedLogo.opacity / 100 }}
                    />
                  </div>

                  {/* Logo Opacity */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-slate-300">Opacity</label>
                      <span className="text-sm font-bold text-white">{selectedLogo.opacity}%</span>
                    </div>
                    <Slider
                      value={[selectedLogo.opacity]}
                      onValueChange={(value) => updateSelectedLogo({ opacity: value[0] })}
                      min={0}
                      max={100}
                      step={1}
                      className="w-full"
                    />
                  </div>

                  {/* Logo Size */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-slate-300">Size</label>
                      <span className="text-sm font-bold text-white">{selectedLogo.size}%</span>
                    </div>
                    <Slider
                      value={[selectedLogo.size]}
                      onValueChange={(value) => updateSelectedLogo({ size: value[0] })}
                      min={5}
                      max={50}
                      step={1}
                      className="w-full"
                    />
                  </div>

                  {/* Logo Rotation */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-slate-300">Rotation</label>
                      <span className="text-sm font-bold text-white">{selectedLogo.rotation}°</span>
                    </div>
                    <Slider
                      value={[selectedLogo.rotation]}
                      onValueChange={(value) => updateSelectedLogo({ rotation: value[0] })}
                      min={0}
                      max={359}
                      step={1}
                      className="w-full"
                    />
                  </div>

                  {/* Tile Logo Toggle */}
                  <div className="flex items-center justify-between p-2 bg-slate-700/30 rounded-lg border border-slate-600">
                    <label className="text-xs font-medium text-white">Tile Pattern</label>
                    <button
                      onClick={() => updateSelectedLogo({ tiled: !selectedLogo.tiled })}
                      className={`relative w-10 h-5 rounded-full transition-colors ${
                        selectedLogo.tiled ? 'bg-cyan-500' : 'bg-slate-600'
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                          selectedLogo.tiled ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Logo Actions */}
                  <div className="flex gap-2">
                    <Button
                      onClick={handleCopyLogo}
                      size="sm"
                      className="flex-1 bg-slate-700 hover:bg-slate-600 text-white"
                      title="Copy logo"
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copy
                    </Button>
                    <Button
                      onClick={handlePasteLogo}
                      size="sm"
                      disabled={!copiedLogo}
                      className="flex-1 bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-50"
                      title="Paste as new logo"
                    >
                      <Clipboard className="w-3 h-3 mr-1" />
                      Paste
                    </Button>
                    <Button
                      onClick={handleRemoveLogo}
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-white"
                      title="Remove logo"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>

                  <p className="text-xs text-slate-400">
                    💡 Click logo on canvas to select, then drag to reposition
                  </p>
                </>
              )}
                </div>
              )}
            </div>

            {/* Quick Tips - Collapsible */}
            <div className="bg-gradient-to-br from-blue-900/30 to-cyan-900/30 backdrop-blur-sm border border-blue-700/30 rounded-xl shadow-xl overflow-hidden">
              <button
                onClick={() => toggleSection('tips')}
                className="w-full flex items-center justify-between p-3 hover:bg-blue-900/20 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {collapsedSections.tips ? (
                    <ChevronRight className="w-4 h-4 text-blue-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-blue-400" />
                  )}
                  <h3 className="text-xs font-bold text-blue-300 uppercase tracking-wider">
                    Quick Tips
                  </h3>
                </div>
              </button>
              
              {!collapsedSections.tips && (
                <div className="px-3 pb-3">
                  <ul className="text-xs text-slate-300 space-y-1 leading-relaxed">
                    <li>• Use ambient slider for daylight to night</li>
                    <li>• Pick presets (2700K-4000K) or custom RGB</li>
                    <li>• Drag lights to reposition them</li>
                    <li>• Copy & paste lights to duplicate</li>
                    <li>• Click Preview to see final result</li>
                  </ul>
                </div>
              )}
            </div>
            </div>
          </div>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />
      
      <input
        ref={loadProjectInputRef}
        type="file"
        accept=".json,application/json"
        onChange={handleLoadProject}
        className="hidden"
      />
      
      <input
        ref={logoInputRef}
        type="file"
        accept="image/*"
        onChange={handleLogoUpload}
        className="hidden"
      />
    </div>
  );
};

export default LightingStudio;